import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, catchError, finalize, forkJoin, map, of, switchMap, take } from 'rxjs';
import { SubjectScheduleService } from 'src/app/services/api/subject-schedules/subject-schedule.service';
import { SubjectService } from 'src/app/services/api/subjects/subject.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { UserManagementService } from 'src/app/services/api/user-management/user-management.service';
import { ScheduleCorrectionRequestsService } from 'src/app/services/api/schedule-correction-requests/schedule-correction-requests.service';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { SweetAlertService } from 'src/app/services/sweet-alert-service/sweet-alert.service';
import {
  ProjectResearchSubjectUpdatePayload,
  ProjectResearchAssignmentDetail,
  ProjectResearchSubjectDetail,
  ProjectResearchTeachingTimeDetail,
  ScheduleSubmissionUserOption,
  ScheduleSubmissionUsersParams,
  StudyModeModel,
  SubjectScheduleGroupAssignment,
  SubjectScheduleGroupCreatePayload,
  SubjectScheduleAssignmentDetail,
  SubjectScheduleGroupDetail,
  SubjectScheduleGroupProjectTeachingTime,
  SubjectScheduleStatusUpdatedByDetail,
  SubjectScheduleGroupTeachingTime,
  SubjectScheduleGroupCreateResponse,
  SubjectScheduleTeachingDateDetail,
  SubjectScheduleTeachingTimeDetail,
} from 'src/app/services/models/subject-schedules/subject-schedule.model';
import {
  SectionTeacherRow,
  SectionTeacherProjectTeachingTime,
  SubjectCreateOption,
  SubjectManagementRow,
  SubjectTypeOption,
  TeacherOption as SharedTeacherOption,
  TeacherWorkloadMode,
  TeachingRangeOption,
  TeachingTypeModel,
  TeachingTypeOption,
} from 'src/app/services/models/subjects/subject.model';
import { AcademicTermModel } from 'src/app/services/models/study-period/study-period.model';
import { ScheduleCorrectionRequestModel } from 'src/app/services/models/schedule/schedule-correction-requests.model';
import { UserManagementModel } from 'src/app/services/models/user-management/user-management.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { SharedModule } from 'src/shared/shared.module';
import { SUBJECT_MANAGEMENT_VIEW_ONLY_ROLE_IDS } from 'src/utils/enum/role.enum';
import { SectionEditorComponent } from '../subject-management-create/components/section-editor/section-editor.component';
import { ProjectTeacherModalComponent } from '../subject-management-create/components/project-teacher-modal/project-teacher-modal.component';
import { SubjectInfoFormComponent } from '../subject-management-create/components/subject-info-form/subject-info-form.component';
import { TeacherManagementModalComponent } from '../subject-management-create/components/teacher-management-modal/teacher-management-modal.component';
import {
  createProjectTeachingTimeForm,
  createSectionForm,
  createTeachingTimeForm,
  updateSectionDuplicateErrors,
} from '../subject-management-create/forms/subject-management-create.forms';
import {
  parseTeachingRangeIsoDate,
  teachingRangeMatchesTime,
} from '../subject-management-create/domain/subject-management-create.utils';
import {
  buildTeachingRangeOptions,
  buildProjectTeacherRow,
  calculateRegularTeacherWorkloadHours,
  calculateProjectWorkloadHours,
  mergeTeachingRangeOptions,
  normalizeTeachingRangeValuesToOptions,
  sortProjectTeachingTimes,
  TeachingTimeValue,
} from '../subject-management-create/domain/subject-management-create-teacher.builder';
import {
  buildSelectedTeachingDateLabel,
  canEditDetailsSection,
  getSubjectManagementDetailsApiErrorMessage,
} from './subject-management-details.adapter';
import {
  mapSectionAggregatedStatus,
  mapSubjectManagementDetailMainStatus,
  mapTeachingApprovalStatus,
  normalizeSubjectManagementStatus,
  normalizeTeachingApprovalStatus,
} from '../subject-management-status.mapper';

type SubjectStatus =
  | 'draft'
  | 'in_progress'
  | 'special_sent_back'
  | 'sent_back'
  | 'pending'
  | 'pending_approval'
  | 'approved';

type SubjectDetailSourceType = 'subject_schedule' | 'project_research_subject';
type SubjectDetailSourceOrigin = 'created' | 'reg';
type SubjectSectionType = 'regular' | 'project_research';
type ControlInputValue = Event | string | number | boolean | null | undefined;

export const EDITABLE_ASSIGNMENT_STATUSES = [
  'imported_pending_fix',
  'draft',
  'revision_requested',
  'special_revision_requested',
] as const;

const HEAD_APPROVED_ASSIGNMENT_STATUSES = [
  'education_head_confirmed',
  'vice_dean_confirmed',
  'approved',
  'completed',
  'finished',
  'locked',
] as const;

const ASSIGNMENT_STATUS_LABEL_CODES: Record<string, string> = {
  'แบบร่าง': 'draft',
  'รอดำเนินการ': 'ready_for_teacher',
  'ส่งกลับแก้ไข': 'revision_requested',
  'ส่งกลับแก้ไขพิเศษ': 'special_revision_requested',
  'รับรองแล้ว': 'approved',
};

function normalizeAssignmentStatus(status: string | null | undefined): string {
  const value = String(status ?? '').trim();

  return ASSIGNMENT_STATUS_LABEL_CODES[value] ?? value.toLowerCase();
}

// This is deliberately permission-only. Display badges continue to use the
// established status mapper and are not affected by this normalization.
export function isEditableAssignmentStatus(status: string | null | undefined): boolean {
  return EDITABLE_ASSIGNMENT_STATUSES.includes(
    normalizeAssignmentStatus(status) as typeof EDITABLE_ASSIGNMENT_STATUSES[number]
  );
}

export function isTeacherModalRowEditableStatus(status: string | null | undefined): boolean {
  return normalizeAssignmentStatus(status) === 'ready_for_teacher'
    || isEditableAssignmentStatus(status);
}

export function isAssignmentApprovedByHead(status: string | null | undefined): boolean {
  return HEAD_APPROVED_ASSIGNMENT_STATUSES.includes(
    normalizeAssignmentStatus(status) as typeof HEAD_APPROVED_ASSIGNMENT_STATUSES[number]
  );
}

interface SubjectTeachingDay {
  subjectScheduleId?: number | null;
  teachingTimeKey?: string | null;
  teachingTypeId?: number | null;
  day: string;
  startTime: string;
  endTime: string;
  time: string;
  isNew: boolean;
}

interface SubjectTeacherRow {
  index: number;
  userId?: number | null;
  scheduleSubmissionId?: number | null;
  isUnsaved?: boolean;
  fullName: string;
  department: string;
  teachingDates: string[];
  teachingRanges: string[];
  selectedTeachingDates?: SubjectScheduleTeachingDateDetail[];
  teachingHours: string;
  updatedAt: string;
  updatedBy: string;
  approvalStatus: string;
  approvalStatusType: SubjectStatus;
  approvalStatusCode?: string;
  returnReason: string;
  description?: string;
  primaryAdvisorStudentCount?: number;
  coAdvisorStudentCount?: number;
  showInTimetable?: boolean;
  projectTeachingTimes?: SubjectScheduleGroupProjectTeachingTime[];
}

interface SubjectSection {
  id: number | null;
  academicTermId: number | null;
  subjectId: number | null;
  curriculumTypeId: number | null;
  teachingTypeId?: number | null;
  sectionValue: string;
  title: string;
  sourceOrigin: SubjectDetailSourceOrigin;
  sectionType: SubjectSectionType;
  rawStatus: string;
  teachingType: string;
  status: SubjectStatus;
  statusLabel: string;
  expanded: boolean;
  room: string;
  studentCount: string;
  workloadHours: string;
  teachingDays: SubjectTeachingDay[];
  workloadCalculation: string;
  teachers: SubjectTeacherRow[];
  validationSubmitted?: boolean;
  teachingTimesError?: 'required' | 'timeOrder' | 'timeOverlap' | '';
  isNew?: boolean;
}

interface SubjectDetail {
  id: number;
  sourceOrigin: SubjectDetailSourceOrigin;
  academicTerm: string;
  year: string;
  subjectType: string;
  code: string;
  name: string;
  curriculum: string;
  creditDescription: string;
  status: SubjectStatus;
  statusLabel: string;
  sections: SubjectSection[];
}

interface TeacherOption extends SharedTeacherOption {
  scheduleSubmissionId?: number | null;
}

interface TeacherDraftRow {
  userId: number | null;
  department: string;
  teachingRange: string[];
  statusLabel: string;
  statusType: SubjectStatus;
  statusCode: string;
  isUnsaved: boolean;
}

type SubjectDetailsSectionTeacherRow = SectionTeacherRow & {
  scheduleSubmissionId?: number | null;
  statusCode?: string | null;
};

type TeacherDraftForm = FormGroup<{
  userId: FormControl<number | null>;
  department: FormControl<string>;
  teachingRange: FormControl<string[]>;
  statusLabel: FormControl<string>;
  statusType: FormControl<SubjectStatus>;
  statusCode: FormControl<string>;
  isUnsaved: FormControl<boolean>;
}>;

type SubjectDetailsPendingDelete =
  | { type: 'sectionTeachingTime'; sectionIndex: number; itemIndex: number }
  | { type: 'projectTeachingTime'; itemIndex: number }
  | { type: 'projectTeacher'; sectionIndex: number; itemIndex: number }
  | { type: 'teacherDraft'; itemIndex: number };

@Component({
  selector: 'app-subject-management-details',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    TranslateModule,
    ProjectTeacherModalComponent,
    SectionEditorComponent,
    SubjectInfoFormComponent,
    TeacherManagementModalComponent,
  ],
  templateUrl: './subject-management-details.component.html',
  styleUrl: './subject-management-details.component.scss',
})
export class SubjectManagementDetailsComponent implements OnInit {
  
  @ViewChild('submitApprovalConfirmDialog')
  private submitApprovalConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('submitApprovalSuccessDialog')
  private submitApprovalSuccessDialog?: ConfirmDialogComponent;

  @ViewChild('submitApprovalErrorDialog')
  private submitApprovalErrorDialog?: ConfirmDialogComponent;

  @ViewChild('deleteItemWarningDialog')
  private deleteItemWarningDialog?: ConfirmDialogComponent;

  @ViewChild('saveSectionBeforeManageTeachersConfirmDialog')
  private saveSectionBeforeManageTeachersConfirmDialog?: ConfirmDialogComponent;

  @ViewChild(TeacherManagementModalComponent)
  private teacherManagementModal?: TeacherManagementModalComponent;
  @ViewChild(ProjectTeacherModalComponent)
  private projectTeacherModal?: ProjectTeacherModalComponent;

  readonly teachingDayOptions = ['วันจันทร์', 'วันอังคาร', 'วันพุธ', 'วันพฤหัสบดี', 'วันศุกร์', 'วันเสาร์', 'วันอาทิตย์'];
  timeOptions = [
    '7:30',
    '8:00',
    '8:30',
    '9:00',
    '9:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '12:00',
    '12:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
    '18:00',
    '18:30',
    '19:00',
    '19:30',
    '20:00',
    '20:30',
    '21:00',
  ];
  readonly fallbackTeachingTypeOptions = ['บรรยาย', 'ปฏิบัติ', 'บรรยาย / ปฏิบัติ'];
  subject?: SubjectDetail;
  loading = false;
  errorMessage = '';
  submitApprovalErrorMessage = '';
  submitApprovalSuccessMessage = 'ดำเนินการส่งขอรับรองสำเร็จ';
  correctionRequestId: number | null = null;
  correctionRequest: ScheduleCorrectionRequestModel | null = null;
  correctionRequestLoading = false;
  correctionRequestErrorMessage = '';
  teacherOptions: TeacherOption[] = [];
  teacherTeachingRangeOptions: TeachingRangeOption[] = [];
  teacherModalErrorMessage = '';
  activeTeacherSection: SubjectSection | null = null;
  teacherModalForm = new FormGroup({
    workloadMode: new FormControl<TeacherWorkloadMode>('shared', { nonNullable: true }),
    teachers: new FormArray<TeacherDraftForm>([]),
  });
  teachingTypeOptions: TeachingTypeOption[] = [];
  subjectTypeOptions: SubjectTypeOption[] = [];
  isSubmittingApproval = false;
  projectTeacherSubmitted = false;
  activeProjectSectionIndex: number | null = null;
  editingProjectTeacherIndex: number | null = null;
  pendingDelete: SubjectDetailsPendingDelete | null = null;
  sectionForms: FormGroup[] = [];
  isSavingNewSection = false;
  addSectionErrorMessage = '';
  private pendingSaveBeforeManageTeachersIndex: number | null = null;
  private pendingOpenTeacherModalSectionValue: string | null = null;
  sectionTeacherRows: Record<number, SubjectDetailsSectionTeacherRow[]> = {};
  subjectOptions: SubjectCreateOption[] = [];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  existingTeachingTimeCounts: Record<number, number> = {};
  
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private subjectService = inject(SubjectService);

  private academicTermStartDate = '';
  private academicTermEndDate = '';
  private academicTermMidtermStartDate = '';
  private academicTermMidtermEndDate = '';
  private academicTermFinalStartDate = '';
  private academicTermFinalEndDate = '';
  private isHydratingTeacherModal = false;
  private pendingApprovalSection: SubjectSection | null = null;
  private readonly digitsOnlyValidators = [Validators.required, Validators.pattern(/^\d+$/)];
  private readonly thaiDayIndexes: Partial<Record<string, number>> = {
    วันอาทิตย์: 0,
    วันจันทร์: 1,
    วันอังคาร: 2,
    วันพุธ: 3,
    วันพฤหัสบดี: 4,
    วันศุกร์: 5,
    วันเสาร์: 6,
  };
  private readonly teachingDateSortOrder: Partial<Record<number, number>> = {
    1: 0,
    2: 1,
    3: 2,
    4: 3,
    5: 4,
    6: 5,
    0: 6,
    7: 6,
  };

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly subjectScheduleService = inject(SubjectScheduleService);
  private readonly studyPeriodService = inject(StudyPeriodService);
  private readonly userManagementService = inject(UserManagementService);
  private readonly correctionRequestsService = inject(ScheduleCorrectionRequestsService);
  private readonly authService = inject(AuthService);
  private readonly sweetAlertService = inject(SweetAlertService);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  subjectForm = this.fb.group({
    academicTerm: [{ value: '', disabled: true }],
    year: [{ value: '', disabled: true }],
    subjectType: [{ value: null as number | string | null, disabled: true }],
    subjectCode: [{ value: null as number | string | null, disabled: true }],
    subjectName: [{ value: '', disabled: true }],
    curriculum: [{ value: '', disabled: true }],
    creditDescription: [{ value: '', disabled: true }],
  });
  // eslint-disable-next-line @typescript-eslint/member-ordering
  projectTeacherForm = this.fb.group({
    userId: [null as number | null, Validators.required],
    department: [{ value: '', disabled: true }],
    teachingTimes: this.fb.array<FormGroup>([createProjectTeachingTimeForm(this.fb)]),
    workloadHours: [{ value: '0', disabled: true }],
    primaryStudentCount: ['', Validators.required],
    coStudentCount: ['', Validators.required],
    description: ['', Validators.required],
    showInSchedule: [false],
  });

  get teacherDraftRows(): FormArray<TeacherDraftForm> {
    return this.teacherModalForm.controls.teachers;
  }

  get projectTeachingTimes(): FormArray<FormGroup> {
    return this.projectTeacherForm.get('teachingTimes') as FormArray<FormGroup>;
  }

  get pendingDeleteMessage(): string {
    return this.pendingDelete?.type === 'teacherDraft' || this.pendingDelete?.type === 'projectTeacher'
      ? 'คุณต้องการลบผู้สอนนี้ใช่หรือไม่ ?'
      : 'คุณต้องการลบวันและเวลาสอนนี้ใช่หรือไม่ ?';
  }

  get isSubjectManagementViewOnly(): boolean {
    const activeRoleId = this.authService.getActiveRoleId();

    return activeRoleId != null &&
      SUBJECT_MANAGEMENT_VIEW_ONLY_ROLE_IDS.some((roleId) => roleId === activeRoleId);
  }

  ngOnInit(): void {
    this.teacherModalForm.controls.workloadMode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onTeacherWorkloadModeChanged());

    const id = Number(this.route.snapshot.queryParamMap.get('id'));
    const correctionRequestId = Number(this.route.snapshot.queryParamMap.get('correction_request_id'));
    this.correctionRequestId = Number.isFinite(correctionRequestId) && correctionRequestId > 0
      ? correctionRequestId
      : null;
    if (this.correctionRequestId !== null) {
      this.loadCorrectionRequest(this.correctionRequestId);
    }

    if (!Number.isFinite(id) || id <= 0) {
      this.navigateBack();
      return;
    }

    const fallbackSubject = this.getSubjectFromNavigationState(id) ?? this.createEmptySubject(id);
    this.subject = fallbackSubject;
    this.loadDisplayOptions(id, fallbackSubject);
  }

  onManageTeachers(section: SubjectSection): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    if (!this.canEditSection(section) || !this.isRegularSection(section)) {
      return;
    }

    section.validationSubmitted = true;

    if (!this.isSectionReadyForTeacherModal(section)) {
      this.requestViewUpdate();
      return;
    }

    this.openTeacherManagementForSection(section);
  }

  onSubmitApproval(section: SubjectSection): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    if (!this.isSectionReadyForApproval(section)) {
      this.requestViewUpdate();
      return;
    }

    this.errorMessage = '';
    this.pendingApprovalSection = section;
    this.submitApprovalConfirmDialog?.open();
  }

  onConfirmSubmitApproval(): void {
    if (this.isSubjectManagementViewOnly) {
      this.submitApprovalConfirmDialog?.close();
      return;
    }

    const section = this.pendingApprovalSection;

    if (section == null || this.isSubmittingApproval) {
      return;
    }

    if (this.isSpecialCorrectionSection(section) && this.correctionRequestId == null) {
      this.submitApprovalConfirmDialog?.close();
      this.submitApprovalErrorMessage = this.correctionRequestLoading
        ? 'กำลังโหลดคำขอแก้ไขกรณีพิเศษ กรุณารอสักครู่แล้วลองอีกครั้ง'
        : 'ไม่พบคำขอแก้ไขกรณีพิเศษที่ได้รับอนุมัติสำหรับรายวิชานี้';
      this.submitApprovalErrorDialog?.open();
      return;
    }

    const request = this.buildSubmitApprovalRequest(section);

    if (request == null) {
      section.validationSubmitted = true;
      this.errorMessage = 'ไม่สามารถส่งขอรับรองได้ กรุณาตรวจสอบข้อมูล Section และช่วงวันที่สอนของผู้สอน';
      this.pendingApprovalSection = null;
      this.submitApprovalConfirmDialog?.close();
      this.requestViewUpdate();
      return;
    }

    this.isSubmittingApproval = true;
    request.pipe(take(1)).subscribe({
      next: (response) => {
        if (this.shouldShowNoChangesWarning(section, response)) {
          this.isSubmittingApproval = false;
          this.pendingApprovalSection = null;
          this.submitApprovalConfirmDialog?.close();
          void this.sweetAlertService.swalWarning(
            'ไม่มีการเปลี่ยนแปลง',
            'ไม่พบการเปลี่ยนแปลงข้อมูลรายวิชาหรือผู้สอน จึงไม่สามารถส่งคำขอรับรองได้'
          );
          this.requestViewUpdate();
          return;
        }

        this.markSectionApprovalSubmitted(section, response);
        this.submitApprovalSuccessMessage = this.correctionRequestId == null
          ? 'ดำเนินการส่งขอรับรองสำเร็จ'
          : 'บันทึกการแก้ไขแล้ว รายวิชาที่ได้รับผลกระทบต้องผ่านการรับรองอีกครั้ง';
        this.submitApprovalConfirmDialog?.close();
        this.reloadCurrentSubjectDetails(() => {
          this.isSubmittingApproval = false;
          this.pendingApprovalSection = null;
          this.submitApprovalSuccessDialog?.open();
          this.requestViewUpdate();
        });
      },
      error: (error: unknown) => {
        this.isSubmittingApproval = false;
        this.pendingApprovalSection = null;
        this.submitApprovalConfirmDialog?.close();
        this.submitApprovalErrorMessage = getSubjectManagementDetailsApiErrorMessage(
          error,
          'ไม่สามารถส่งขอรับรองได้ กรุณาลองใหม่อีกครั้ง'
        );
        this.cdr.detectChanges();
        this.submitApprovalErrorDialog?.open();
        this.requestViewUpdate();
      },
    });
  }

  onCancelSubmitApproval(): void {
    this.pendingApprovalSection = null;
  }

  isSectionReadyForApproval(section: SubjectSection): boolean {
    if (!this.canEditSection(section) || this.isSubmittingApproval) {
      return false;
    }

    section.validationSubmitted = true;
    this.errorMessage = '';

    if (this.isRegularSection(section) && !this.validateSection(section)) {
      return false;
    }

    if (!this.hasSelectedTeachingDatesForSection(section)) {
      this.errorMessage = 'กรุณาจัดการผู้สอนและเลือกช่วงวันที่สอนให้ตรงกับวันสอนก่อนส่งขอรับรอง';
      return false;
    }

    return true;
  }

  isRegSection(section: SubjectSection): boolean {
    return section.sourceOrigin === 'reg';
  }

  isRegularSection(section: SubjectSection): boolean {
    return section.sectionType === 'regular';
  }

  isProjectResearchSection(section: SubjectSection): boolean {
    return section.sectionType === 'project_research';
  }


  canEditSection(_section: SubjectSection): boolean {
    return !this.isSubjectManagementViewOnly && canEditDetailsSection();
  }

  isSectionFieldsEditable(section: SubjectSection): boolean {
    return this.canEditSection(section);
  }

  getTeachingTypeOptions(section: SubjectSection): string[] {
    const currentValue = this.displayValue(section.teachingType);
    const optionLabels = this.teachingTypeOptions
      .map((option) => this.displayValue(option.label))
      .filter((label) => label !== '-');
    const baseOptions = optionLabels.length > 0 ? optionLabels : this.fallbackTeachingTypeOptions;
    const values = currentValue === '-'
      ? [...baseOptions]
      : [currentValue, ...baseOptions];

    return Array.from(new Set(values));
  }

    

  getTeachingDayOptions(teachingDay: SubjectTeachingDay): string[] {
    return this.withCurrentOption(teachingDay.day, this.teachingDayOptions);
  }

  getTimeOptions(value: string): string[] {
    return this.withCurrentOption(value, this.timeOptions);
  }

  canEditTeachingTime(section: SubjectSection, teachingDay: SubjectTeachingDay): boolean {
    return this.isSectionFieldsEditable(section) && Boolean(teachingDay.isNew);
  }

  getSectionFieldError(
    section: SubjectSection,
    field: 'sectionValue' | 'teachingType' | 'room' | 'studentCount' | 'workloadHours',
  ): { required: true } | null {
    if (section.validationSubmitted !== true) {
      return null;
    }

    if (field === 'workloadHours') {
      return null;
    }

    return this.isBlank(section[field]) ? { required: true } : null;
  }

  getTeachingTimeFieldError(
    section: SubjectSection,
    teachingDay: SubjectTeachingDay,
    field: 'day' | 'startTime' | 'endTime',
  ): { required: true } | null {
    if (section.validationSubmitted !== true) {
      return null;
    }

    return this.isBlank(teachingDay[field]) ? { required: true } : null;
  }

  getTeachingTimesError(section: SubjectSection): string {
    if (section.validationSubmitted !== true) {
      return '';
    }

    switch (section.teachingTimesError) {
      case 'required':
        return 'กรุณากรอกข้อมูลวันและเวลาสอนให้ครบ';
      case 'timeOrder':
        return 'เวลาเริ่มต้องน้อยกว่าเวลาสิ้นสุด';
      case 'timeOverlap':
        return 'วันและเวลาสอนทับซ้อนกัน';
      case '':
      case undefined:
        return '';
      default:
        return '';
    }
  }

  onTeacherWorkloadModeChanged(): void {
    if (this.isHydratingTeacherModal || this.activeTeacherSection == null) {
      return;
    }

    if (this.shouldDisableWorkloadModeChange()) {
      this.teacherModalForm.controls.workloadMode.setValue(
        this.activeTeacherSection.workloadCalculation === 'ไม่แบ่งภาระงาน' ? 'notShared' : 'shared',
        { emitEvent: false },
      );
      this.syncTeacherModalControlStates();
      return;
    }

    this.resetTeacherDraftRows([]);
    this.requestViewUpdate();
  }

  onAddTeacherDraftRow(): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    this.teacherModalErrorMessage = '';
    this.teacherDraftRows.push(this.createTeacherDraftForm());
    this.syncTeacherModalControlStates();
    this.requestViewUpdate();
  }

  onRemoveTeacherDraftRow(index: number): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    if (index < 0 || index >= this.teacherDraftRows.length || !this.isTeacherDraftRowEditable(index)) {
      return;
    }

    this.requestPendingDelete({ type: 'teacherDraft', itemIndex: index });
  }

  onTeacherSelected(index: number, option: SharedTeacherOption | undefined): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    if (!this.isTeacherDraftRowEditable(index)) {
      return;
    }

    const row = this.teacherDraftRows.at(index);

    row.patchValue({
      department: option?.department ?? '-',
    }, { emitEvent: false });

    this.teacherModalErrorMessage = '';
    this.requestViewUpdate();
  }

  onManageTeachersByIndex(sectionIndex: number): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    const section = this.subject?.sections[sectionIndex];

    if (section == null) {
      return;
    }

    if (this.isNewSectionAt(sectionIndex)) {
      this.pendingSaveBeforeManageTeachersIndex = sectionIndex;
      this.saveSectionBeforeManageTeachersConfirmDialog?.open();
      return;
    }

    this.syncSectionFromForm(sectionIndex);
    if (section.sectionType === 'project_research') {
      this.openProjectTeacher(sectionIndex);
      return;
    }
    if (!this.isSharedSectionReadyForTeacherModal(sectionIndex)) {
      this.markSharedSectionForTeacherModalValidation(sectionIndex);
      this.requestViewUpdate();
      return;
    }
    this.onManageTeachers(section);
  }

  onConfirmSaveSectionBeforeManageTeachers(): void {
    const sectionIndex = this.pendingSaveBeforeManageTeachersIndex;

    if (this.isSubjectManagementViewOnly || sectionIndex == null) {
      this.pendingSaveBeforeManageTeachersIndex = null;
      this.saveSectionBeforeManageTeachersConfirmDialog?.close();
      return;
    }

    this.onSaveNewSectionByIndex(sectionIndex, true);
  }

  onCancelSaveSectionBeforeManageTeachers(): void {
    this.pendingSaveBeforeManageTeachersIndex = null;
  }

  onSubmitApprovalByIndex(sectionIndex: number): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    const section = this.subject?.sections[sectionIndex];

    if (section != null) {
      this.syncSectionFromForm(sectionIndex);
      this.syncProjectTeachers(sectionIndex);
      this.onSubmitApproval(section);
    }
  }

  // ENOT-160: add another section to an existing subject (manual or REG-synced),
  // saved directly without going through the certification flow.
  onAddSection(): void {
    if (this.isSubjectManagementViewOnly || this.subject == null) {
      return;
    }

    const section = this.shouldAddProjectResearchSection()
      ? this.createDraftProjectResearchSection()
      : this.createDraftRegularSection();

    if (section.academicTermId == null || section.subjectId == null) {
      this.addSectionErrorMessage = 'ไม่สามารถเพิ่ม Section ได้ กรุณาตรวจสอบข้อมูลรายวิชา';
      this.requestViewUpdate();
      return;
    }

    section.isNew = true;
    section.validationSubmitted = false;

    this.subject.sections.push(section);
    this.sectionForms.push(this.createSharedSectionForm(section));
    const newIndex = this.sectionForms.length - 1;
    this.existingTeachingTimeCounts[newIndex] = 0;
    this.sectionTeacherRows[newIndex] = [];
    this.addSectionErrorMessage = '';
    this.requestViewUpdate();
  }

  isNewSectionAt(sectionIndex: number): boolean {
    return this.subject?.sections[sectionIndex]?.isNew === true;
  }

  onSaveNewSectionByIndex(sectionIndex: number, openTeacherModalAfterSave = false): void {
    if (this.isSubjectManagementViewOnly || this.isSavingNewSection) {
      return;
    }

    const section = this.subject?.sections[sectionIndex];
    const form = this.sectionForms[sectionIndex];

    if (section?.isNew !== true) {
      return;
    }

    const closePendingManageTeachersConfirm = (): void => {
      if (!openTeacherModalAfterSave) {
        return;
      }

      this.pendingSaveBeforeManageTeachersIndex = null;
      this.saveSectionBeforeManageTeachersConfirmDialog?.close();
    };

    section.validationSubmitted = true;
    form.markAllAsTouched();

    if (form.invalid) {
      closePendingManageTeachersConfirm();
      this.requestViewUpdate();
      return;
    }

    this.syncSectionFromForm(sectionIndex);

    if (this.isDuplicateSection(sectionIndex, section)) {
      this.addSectionErrorMessage =
        `Section ${section.sectionValue} และประเภทการสอนนี้มีอยู่แล้วในรายวิชานี้ กรุณาเปลี่ยนหมายเลข Section หรือประเภทการสอน`;
      closePendingManageTeachersConfirm();
      this.requestViewUpdate();
      return;
    }

    const payload = this.buildNewSectionPayload(section);

    if (payload == null) {
      this.addSectionErrorMessage = 'ไม่สามารถบันทึก Section ได้ กรุณาตรวจสอบข้อมูล Section และวันเวลาสอน';
      closePendingManageTeachersConfirm();
      this.requestViewUpdate();
      return;
    }

    this.isSavingNewSection = true;
    this.addSectionErrorMessage = '';
    this.requestViewUpdate();

    const sectionValueToReopen = section.sectionValue;

    this.subjectScheduleService.createSubjectScheduleGroup(payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isSavingNewSection = false;
          if (openTeacherModalAfterSave) {
            this.pendingSaveBeforeManageTeachersIndex = null;
            this.saveSectionBeforeManageTeachersConfirmDialog?.close();
            this.pendingOpenTeacherModalSectionValue = sectionValueToReopen;
          }
          this.reloadCurrentSubjectDetails();
          this.requestViewUpdate();
        },
        error: (error: unknown) => {
          this.isSavingNewSection = false;
          this.addSectionErrorMessage = getSubjectManagementDetailsApiErrorMessage(
            error,
            'ไม่สามารถบันทึก Section ได้ กรุณาลองใหม่อีกครั้ง',
          );
          closePendingManageTeachersConfirm();
          this.requestViewUpdate();
        },
      });
  }

  onDiscardNewSection(sectionIndex: number): void {
    if (!this.isNewSectionAt(sectionIndex) || this.subject == null) {
      return;
    }

    this.subject.sections.splice(sectionIndex, 1);
    this.sectionForms.splice(sectionIndex, 1);
    this.existingTeachingTimeCounts = this.shiftIndexRecord(this.existingTeachingTimeCounts, sectionIndex);
    this.sectionTeacherRows = this.shiftIndexRecord(this.sectionTeacherRows, sectionIndex);
    this.addSectionErrorMessage = '';
    this.requestViewUpdate();
  }

  onProjectTeacherSelected(teacher?: SharedTeacherOption): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    this.projectTeacherForm.patchValue({
      department: teacher?.department ?? '-',
    }, { emitEvent: false });
  }

  onAddProjectTeachingTime(): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    this.projectTeachingTimes.push(createProjectTeachingTimeForm(this.fb));
    this.updateProjectWorkloadHours();
  }

  onRemoveProjectTeachingTime(timeIndex: number): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    if (timeIndex > 0 && timeIndex < this.projectTeachingTimes.length) {
      this.requestPendingDelete({ type: 'projectTeachingTime', itemIndex: timeIndex });
    }
  }

  onEditProjectTeacher(sectionIndex: number, teacherIndex: number): void {
    const rows = this.sectionTeacherRows[sectionIndex] ?? [];

    if (
      teacherIndex < 0
      || teacherIndex >= rows.length
      || !this.isSectionEditableAt(sectionIndex)
      || !this.isProjectTeacherEditableAt(sectionIndex, teacherIndex)
    ) {
      return;
    }

    this.openProjectTeacher(sectionIndex, teacherIndex, rows[teacherIndex]);
  }

  onRemoveProjectTeacher(sectionIndex: number, teacherIndex: number): void {
    if (!this.isSectionEditableAt(sectionIndex) || !this.isProjectTeacherEditableAt(sectionIndex, teacherIndex)) {
      return;
    }

    const rows = this.sectionTeacherRows[sectionIndex] ?? [];

    if (teacherIndex < 0 || teacherIndex >= rows.length) {
      return;
    }

    this.requestPendingDelete({ type: 'projectTeacher', sectionIndex, itemIndex: teacherIndex });
  }

  onSaveProjectTeacher(): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    this.projectTeacherSubmitted = true;
    this.projectTeacherForm.markAllAsTouched();

    if (this.activeProjectSectionIndex == null || this.projectTeacherForm.invalid) {
      return;
    }

    const row = buildProjectTeacherRow({
      formValue: this.projectTeacherForm.getRawValue(),
      teacherOptions: this.teacherOptions,
      teachingTimes: this.getProjectTeachingTimeValues(),
    });

    if (row == null) {
      return;
    }

    const sectionIndex = this.activeProjectSectionIndex;
    const rows = [...(this.sectionTeacherRows[sectionIndex] ?? [])];
    const existingRow = this.editingProjectTeacherIndex == null ? null : rows[this.editingProjectTeacherIndex];
    const existingScheduleSubmissionId = existingRow?.userId === row.userId
      ? existingRow.scheduleSubmissionId
      : null;
    const rowToSave: SubjectDetailsSectionTeacherRow = {
      ...row,
      status: existingRow?.status ?? 'แบบร่าง',
      statusType: existingRow?.statusType ?? 'draft',
      statusCode: existingRow?.statusCode ?? 'draft',
      scheduleSubmissionId: existingScheduleSubmissionId
        ?? this.teacherOptions.find((option) => option.id === row.userId)?.scheduleSubmissionId
        ?? null,
    };

    if (this.editingProjectTeacherIndex == null) {
      rows.push(rowToSave);
    } else {
      rows[this.editingProjectTeacherIndex] = rowToSave;
    }

    this.sectionTeacherRows[sectionIndex] = rows;
    this.syncProjectTeachers(sectionIndex);
    this.closeProjectTeacherModal();
  }

  onCancelProjectTeacher(): void {
    this.closeProjectTeacherModal();
  }

  updateProjectWorkloadHours(): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    const value = calculateProjectWorkloadHours(
      this.academicTermStartDate,
      this.academicTermEndDate,
      this.getProjectTeachingTimeValues(),
      {
        midtermStartDate: this.academicTermMidtermStartDate,
        midtermEndDate: this.academicTermMidtermEndDate,
        finalStartDate: this.academicTermFinalStartDate,
        finalEndDate: this.academicTermFinalEndDate,
      },
    );
    this.projectTeacherForm.get('workloadHours')?.setValue(value, { emitEvent: false });
  }

  onAddTeachingTimeByIndex(sectionIndex: number): void {
    if (!this.isSectionEditableAt(sectionIndex)) {
      return;
    }

    const teachingTimes = this.sectionForms[sectionIndex]?.get('teachingTimes') as FormArray<FormGroup> | undefined;
    const section = this.subject?.sections[sectionIndex];

    if (teachingTimes == null || section == null) {
      return;
    }

    teachingTimes.push(createTeachingTimeForm(this.fb, false));
    section.teachingDays.push({
      subjectScheduleId: null,
      teachingTimeKey: null,
      teachingTypeId: section.teachingTypeId ?? null,
      day: '',
      startTime: '',
      endTime: '',
      time: '-',
      isNew: true,
    });
  }

  onRemoveTeachingTimeByIndex(sectionIndex: number, timeIndex: number): void {
    if (!this.isSectionEditableAt(sectionIndex) || this.isTeachingTimeUsedAt(sectionIndex, timeIndex)) {
      return;
    }

    const teachingTimes = this.sectionForms[sectionIndex]?.get('teachingTimes') as FormArray<FormGroup> | undefined;

    if (teachingTimes != null && timeIndex > 0 && timeIndex < teachingTimes.length) {
      this.requestPendingDelete({ type: 'sectionTeachingTime', sectionIndex, itemIndex: timeIndex });
    }
  }

  onConfirmPendingDelete(): void {
    if (this.isSubjectManagementViewOnly) {
      this.clearPendingDelete();
      return;
    }

    const pendingDelete = this.pendingDelete;

    if (pendingDelete == null) {
      this.deleteItemWarningDialog?.close();
      return;
    }

    switch (pendingDelete.type) {
      case 'sectionTeachingTime':
        this.removeSectionTeachingTime(pendingDelete.sectionIndex, pendingDelete.itemIndex);
        break;
      case 'projectTeachingTime':
        this.removeProjectTeachingTime(pendingDelete.itemIndex);
        break;
      case 'projectTeacher':
        this.removeProjectTeacher(pendingDelete.sectionIndex, pendingDelete.itemIndex);
        break;
      case 'teacherDraft':
        this.removeTeacherDraftRow(pendingDelete.itemIndex);
        break;
    }

    this.clearPendingDelete();
  }

  onCancelPendingDelete(): void {
    this.pendingDelete = null;
  }

  isProjectResearchSectionAt(sectionIndex: number): boolean {
    return this.subject?.sections[sectionIndex]?.sectionType === 'project_research';
  }

  isSectionEditableAt(sectionIndex: number): boolean {
    return !this.isSubjectManagementViewOnly && canEditDetailsSection();
  }

  isSectionFieldsEditableAt(sectionIndex: number): boolean {
    return this.isSectionEditableAt(sectionIndex);
  }

  isSectionSubmittedAt(sectionIndex: number): boolean {
    return !this.isSectionEditableAt(sectionIndex);
  }

  isSectionExpandedAt(sectionIndex: number): boolean {
    return this.subject?.sections[sectionIndex]?.expanded !== false;
  }

  onSectionExpandedChange(sectionIndex: number, expanded: boolean): void {
    const section = this.subject?.sections[sectionIndex];

    if (section == null) {
      return;
    }

    section.expanded = expanded;
  }

  getExistingTeachingTimeCountAt(sectionIndex: number): number {
    return this.existingTeachingTimeCounts[sectionIndex] ?? 0;
  }

  getSectionStatusLabelAt(sectionIndex: number): string {
    return this.subject?.sections[sectionIndex]?.statusLabel ?? 'รอดำเนินการ';
  }

  getSectionStatusAt(sectionIndex: number): string | null {
    return this.subject?.sections[sectionIndex]?.status ?? 'pending';
  }

  getSectionTeachersAt(sectionIndex: number): SectionTeacherRow[] {
    return this.sectionTeacherRows[sectionIndex] ?? [];
  }

  getUsedTeachingTimeIndexesAt(sectionIndex: number): number[] {
    const section = this.subject?.sections[sectionIndex];

    if (section == null) {
      return [];
    }

    return section.teachingDays
      .map((teachingTime, timeIndex) => this.isTeachingTimeUsed(section, teachingTime) ? timeIndex : -1)
      .filter((timeIndex) => timeIndex >= 0);
  }

  isTeachingTimeUsedAt(sectionIndex: number, timeIndex: number): boolean {
    const section = this.subject?.sections[sectionIndex];
    const teachingTime = section?.teachingDays[timeIndex];

    return section != null && teachingTime != null && this.isTeachingTimeUsed(section, teachingTime);
  }

  // Prefer stable references from selected_teaching_dates; formatted ranges are
  // retained as a fallback for locally-added selections that do not have IDs yet.
  isTeachingTimeUsed(section: SubjectSection, teachingTime: SubjectTeachingDay): boolean {
    const dayOfWeek = this.getDayOfWeek(teachingTime.day);

    if (dayOfWeek == null) {
      return false;
    }

    return section.teachers.some((teacher) => {
      const teachingRanges = this.normalizeTeachingRangeSelection(teacher.teachingRanges);
      const matchesStableReference = (teacher.selectedTeachingDates ?? []).some((selectedDate) => (
        this.selectedTeachingDateMatchesTime(selectedDate, teachingTime)
      ));

      return matchesStableReference || teachingRanges.some((range) => (
        teachingRangeMatchesTime(range, dayOfWeek, teachingTime.startTime, teachingTime.endTime)
      ));
    });
  }

  getTeacherDraftRowEditability(): boolean[] {
    return this.teacherDraftRows.controls.map((_row, index) => this.isTeacherDraftRowEditable(index));
  }

  shouldDisableWorkloadModeChange(): boolean {
    return this.teacherModalForm.controls.workloadMode.value === 'notShared'
      && this.teacherDraftRows.controls.some((_row, index) => this.isTeacherDraftRowApprovedByHead(index));
  }

  getProjectTeacherEditabilityAt(sectionIndex: number): boolean[] {
    return (this.sectionTeacherRows[sectionIndex] ?? [])
      .map((_row, teacherIndex) => this.isProjectTeacherEditableAt(sectionIndex, teacherIndex));
  }

  getSectionWorkloadModeAt(sectionIndex: number): TeacherWorkloadMode {
    return this.subject?.sections[sectionIndex]?.workloadCalculation === 'ไม่แบ่งภาระงาน'
      ? 'notShared'
      : 'shared';
  }

  getSectionWorkloadHoursAt(sectionIndex: number): string {
    return String(this.sectionForms[sectionIndex]?.get('workloadHours')?.value ?? '-');
  }

  getActiveTeacherTeachingTimes(): TeachingTimeValue[] {
    return this.activeTeacherSection?.teachingDays.map((time) => ({
      teachingDay: time.day,
      startTime: time.startTime,
      endTime: time.endTime,
    })) ?? [];
  }

  onCancelTeachers(): void {
    this.closeTeacherModal();
    this.activeTeacherSection = null;
    this.teacherModalErrorMessage = '';
    this.resetTeacherDraftRows([]);
    this.requestViewUpdate();
  }

  onSaveTeachers(): void {
    if (this.isSubjectManagementViewOnly) {
      this.closeTeacherModal();
      return;
    }

    if (this.activeTeacherSection == null) {
      this.closeTeacherModal();
      return;
    }

    if (this.hasTeacherDraftRowWithoutTeachingRange()) {
      this.teacherModalErrorMessage = 'กรุณาเลือกช่วงวันที่สอน';
      this.requestViewUpdate();
      return;
    }

    this.activeTeacherSection.workloadCalculation = this.getSelectedTeacherWorkloadMode() === 'shared'
      ? 'แบ่งภาระงาน'
      : 'ไม่แบ่งภาระงาน';

    this.activeTeacherSection.teachers = this.getTeacherDraftRowsValue()
      .filter((row) => row.userId != null)
      .map((row, index) => this.mapTeacherDraftToRow(row, index));
    this.refreshSharedTeacherRows();
    const sectionIndex = this.subject?.sections.indexOf(this.activeTeacherSection) ?? -1;

    if (sectionIndex >= 0) {
      this.syncTeachingTimeControlStates(sectionIndex);
    }

    this.closeTeacherModal();
    this.activeTeacherSection = null;
    this.teacherModalErrorMessage = '';
    this.resetTeacherDraftRows([]);
    this.requestViewUpdate();
  }

  onSectionFieldInput(
    section: SubjectSection,
    field: 'sectionValue' | 'room' | 'studentCount',
    event: ControlInputValue,
  ): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    const rawValue = this.getEventValue(event);
    const value = field === 'sectionValue' || field === 'studentCount'
      ? this.sanitizeDigits(rawValue)
      : rawValue;

    section[field] = value;

    if (field === 'sectionValue') {
      section.title = `Section : ${this.displayValue(value)}`;
    }

    this.validateSection(section);
    this.onSectionDraftChanged();
  }

  onSectionFieldChange(section: SubjectSection, field: 'teachingType', event: ControlInputValue): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    section[field] = this.getEventValue(event);
    section.teachingTypeId = this.getTeachingTypeIdByLabel(section.teachingType);

    this.validateSection(section);
    this.onSectionDraftChanged();
  }

  onTeachingTimeChange(
    section: SubjectSection,
    teachingDay: SubjectTeachingDay,
    field: 'day' | 'startTime' | 'endTime',
    event: ControlInputValue,
  ): void {
    if (!this.canEditTeachingTime(section, teachingDay)) {
      return;
    }

    teachingDay[field] = this.getEventValue(event);
    teachingDay.time = this.buildTeachingTimeLabel(teachingDay.startTime, teachingDay.endTime);
    this.validateSection(section);
    this.onSectionDraftChanged();
  }

  onAddTeachingTime(section: SubjectSection): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    if (!this.isSectionFieldsEditable(section)) {
      return;
    }

    section.teachingDays.push({
      day: '',
      startTime: '',
      endTime: '',
      time: this.buildTeachingTimeLabel('', ''),
      isNew: true,
    });
    this.validateSection(section);
    this.onSectionDraftChanged();
  }

  onRemoveTeachingTime(section: SubjectSection, index: number): void {
    if (this.isSubjectManagementViewOnly) {
      return;
    }

    const teachingDay = section.teachingDays[index];

    if (!this.canEditTeachingTime(section, teachingDay) || section.teachingDays.length <= 1) {
      return;
    }

    section.teachingDays.splice(index, 1);
    this.validateSection(section);
    this.onSectionDraftChanged();
  }

  onSectionDraftChanged(): void {
    this.requestViewUpdate();
  }

  private isSectionReadyForTeacherModal(section: SubjectSection): boolean {
    return this.validateSection(section);
  }

  private validateSection(section?: SubjectSection): boolean {
    if (section == null) {
      return false;
    }

    const hasRequiredError = [
      section.sectionValue,
      section.teachingType,
      section.studentCount,
    ].some((value) => this.isBlank(value));

    const ranges = section.teachingDays
      .map((teachingDay) => {
        if (
          this.isBlank(teachingDay.day)
          || this.isBlank(teachingDay.startTime)
          || this.isBlank(teachingDay.endTime)
        ) {
          return null;
        }

        const start = this.toMinute(teachingDay.startTime);
        const end = this.toMinute(teachingDay.endTime);

        if (start == null || end == null) {
          return null;
        }

        return {
          day: teachingDay.day,
          start,
          end,
        };
      });

    if (section.teachingDays.length === 0 || ranges.some((range) => range == null)) {
      section.teachingTimesError = 'required';
      return false;
    }

    const validRanges = ranges.filter((range): range is { day: string; start: number; end: number } => range != null);

    if (validRanges.some((range) => range.start >= range.end)) {
      section.teachingTimesError = 'timeOrder';
      return false;
    }

    for (let i = 0; i < validRanges.length; i += 1) {
      for (let j = i + 1; j < validRanges.length; j += 1) {
        const current = validRanges[i];
        const next = validRanges[j];
        const isSameDay = current.day === next.day;
        const isOverlapped = current.start < next.end && next.start < current.end;

        if (isSameDay && isOverlapped) {
          section.teachingTimesError = 'timeOverlap';
          return false;
        }
      }
    }

    section.teachingTimesError = '';
    return !hasRequiredError;
  }

  private isTeacherReadyForApproval(section: SubjectSection, teacher: SubjectTeacherRow): boolean {
    if (this.getScheduleSubmissionIdForTeacher(teacher) == null) {
      return false;
    }

    if (this.isRegularSection(section)) {
      return this.normalizeTeachingRangeSelection(teacher.teachingRanges).length > 0;
    }

    return (teacher.projectTeachingTimes ?? []).length > 0;
  }

  private buildSubmitApprovalRequest(
    section: SubjectSection,
  ): Observable<SubjectScheduleGroupCreateResponse | ProjectResearchSubjectDetail> | null {
    if (this.isRegularSection(section)) {
      const payload = this.buildRegularSubmitApprovalPayload(section);

      return payload == null
        ? null
        : section.id == null
          ? this.subjectScheduleService.createSubjectScheduleGroup(payload)
          : this.subjectScheduleService.updateSubjectScheduleGroup(section.id, payload);
    }

    if (section.id == null) {
      const payload = this.buildProjectResearchCreatePayload(section);

      return payload == null
        ? null
        : this.subjectScheduleService.createSubjectScheduleGroup(payload);
    }

    const payload = this.buildProjectResearchSubmitApprovalPayload(section);

    return payload == null
      ? null
      : this.subjectScheduleService.updateProjectResearchSubject(section.id, payload);
  }

  private buildRegularSubmitApprovalPayload(section: SubjectSection): SubjectScheduleGroupCreatePayload | null {
    if (
      section.academicTermId == null
      || section.subjectId == null
      || this.isBlank(section.sectionValue)
    ) {
      return null;
    }

    const teachingTimes = this.buildRegularSubjectTeachingTimes(section);

    if (teachingTimes.length === 0 || this.countSelectedAssignments(teachingTimes) === 0) {
      return null;
    }

    return {
      subject_schedule_group: {
        academic_term_id: section.academicTermId,
        subject_id: section.subjectId,
        ...(section.curriculumTypeId == null ? {} : { curriculum_type_id: section.curriculumTypeId }),
        section: section.sectionValue,
        sections: [{
          section: section.sectionValue,
          is_workload_split: section.workloadCalculation !== 'ไม่แบ่งภาระงาน',
        }],
        status: 'submitted',
        replace_slots: true,
        study_mode_code: 'REG',
        teaching_type_id: this.getTeachingTypeId(section),
        room_name: this.displayOptionalText(section.room) || undefined,
        is_workload_split: section.workloadCalculation !== 'ไม่แบ่งภาระงาน',
        student_count: this.toNumber(section.studentCount),
        ...(this.correctionRequestId == null ? {} : { correction_request_id: this.correctionRequestId }),
        teaching_times: teachingTimes,
      },
    };
  }

  private buildProjectResearchSubmitApprovalPayload(section: SubjectSection): ProjectResearchSubjectUpdatePayload | null {
    if (
      section.academicTermId == null
      || section.subjectId == null
      || this.isBlank(section.sectionValue)
    ) {
      return null;
    }

    const assignments = this.buildProjectResearchAssignments(section);

    if (assignments.length === 0) {
      return null;
    }

    return {
      project_research_subject: {
        academic_term_id: section.academicTermId,
        subject_id: section.subjectId,
        ...(section.curriculumTypeId == null ? {} : { curriculum_type_id: section.curriculumTypeId }),
        section: section.sectionValue,
        status: 'submitted',
        replace_slots: true,
        ...(this.correctionRequestId == null ? {} : { correction_request_id: this.correctionRequestId }),
        assignments,
      },
    };
  }

  private buildProjectResearchCreatePayload(section: SubjectSection): SubjectScheduleGroupCreatePayload | null {
    if (
      section.academicTermId == null
      || section.subjectId == null
      || this.isBlank(section.sectionValue)
    ) {
      return null;
    }

    const assignments = this.buildProjectResearchAssignments(section);

    if (assignments.length === 0) {
      return null;
    }

    return {
      subject_schedule_group: {
        academic_term_id: section.academicTermId,
        subject_id: section.subjectId,
        ...(section.curriculumTypeId == null ? {} : { curriculum_type_id: section.curriculumTypeId }),
        section: section.sectionValue,
        sections: [section.sectionValue],
        status: 'submitted',
        replace_slots: true,
        study_mode_code: this.getProjectResearchStudyModeCode(),
        assignments,
      },
    };
  }

  private buildRegularSubjectTeachingTimes(section: SubjectSection): SubjectScheduleGroupTeachingTime[] {
    return section.teachingDays
      .map((teachingDay): SubjectScheduleGroupTeachingTime | null => {
        const dayOfWeek = this.getDayOfWeek(teachingDay.day);
        const startTime = this.displayOptionalText(teachingDay.startTime);
        const endTime = this.displayOptionalText(teachingDay.endTime);

        if (dayOfWeek == null || !startTime || !endTime) {
          return null;
        }

        const selectedBy = section.teachers
          .map((teacher) => this.buildRegularSubjectAssignment(
            teacher,
            section.sectionValue,
            teachingDay,
            dayOfWeek,
            startTime,
            endTime
          ))
          .filter((assignment): assignment is SubjectScheduleGroupAssignment => assignment != null);

        return {
          ...(teachingDay.subjectScheduleId == null ? {} : { id: teachingDay.subjectScheduleId }),
          ...(teachingDay.teachingTimeKey == null ? {} : { key: teachingDay.teachingTimeKey }),
          day_of_week: dayOfWeek,
          start_time: startTime,
          end_time: endTime,
          teaching_type_id: teachingDay.teachingTypeId ?? this.getTeachingTypeId(section),
          room_name: this.displayOptionalText(section.room) || undefined,
          selected_by: selectedBy.length > 0 ? selectedBy : undefined,
        };
      })
      .filter((time): time is SubjectScheduleGroupTeachingTime => time != null);
  }

  private computeNextSectionValue(): string {
    const numericValues = (this.subject?.sections ?? [])
      .filter((candidate) => candidate.isNew !== true)
      .map((candidate) => Number(candidate.sectionValue))
      .filter((value) => Number.isInteger(value) && value > 0);

    return numericValues.length === 0 ? '1' : String(Math.max(...numericValues) + 1);
  }

  private loadCorrectionRequest(correctionRequestId: number): void {
    this.correctionRequestLoading = true;
    this.correctionRequestErrorMessage = '';
    this.correctionRequestsService.getCorrectionRequest(correctionRequestId)
      .pipe(take(1))
      .subscribe({
        next: (request) => {
          this.correctionRequest = request;
          this.correctionRequestLoading = false;
          this.requestViewUpdate();
        },
        error: () => {
          this.correctionRequest = null;
          this.correctionRequestLoading = false;
          this.correctionRequestErrorMessage = 'ไม่สามารถโหลดรายละเอียดคำขอแก้ไขกรณีพิเศษได้';
          this.requestViewUpdate();
        },
      });
  }

  private resolveActiveCorrectionRequestForSections(sections: SubjectSection[]): void {
    if (this.correctionRequestId !== null || this.correctionRequestLoading) {
      return;
    }

    const section = sections.find((item) => this.isSpecialCorrectionSection(item));
    if (section?.id == null) {
      return;
    }

    const params = this.isRegularSection(section)
      ? { subject_schedule_group_id: section.id }
      : { project_research_subject_id: section.id };

    this.correctionRequestLoading = true;
    this.correctionRequestErrorMessage = '';
    this.correctionRequestsService.getActiveCorrectionRequestForEditing(params)
      .pipe(take(1))
      .subscribe({
        next: (request) => {
          this.correctionRequest = request;
          this.correctionRequestId = request.id;
          this.correctionRequestLoading = false;
          this.requestViewUpdate();
        },
        error: () => {
          this.correctionRequest = null;
          this.correctionRequestId = null;
          this.correctionRequestLoading = false;
          this.correctionRequestErrorMessage = 'ไม่พบคำขอแก้ไขกรณีพิเศษที่ได้รับอนุมัติสำหรับรายวิชานี้';
          this.requestViewUpdate();
        },
      });
  }

  private isSpecialCorrectionSection(section: SubjectSection): boolean {
    return normalizeTeachingApprovalStatus(section.rawStatus) === 'special_revision_requested' ||
      section.teachers.some((teacher) => (
        teacher.approvalStatus === 'ส่งกลับแก้ไขพิเศษ' ||
        teacher.approvalStatusType === 'special_sent_back' ||
        teacher.approvalStatusType === 'sent_back' && teacher.approvalStatus.includes('พิเศษ')
      ));
  }

  private isDuplicateSection(sectionIndex: number, section: SubjectSection): boolean {
    const normalizedSection = this.normalizeSectionForComparison(section.sectionValue);
    const normalizedTeachingType = this.normalizeTeachingTypeForComparison(section);

    return (this.subject?.sections ?? []).some((candidate, index) =>
      index !== sectionIndex
      && candidate.isNew !== true
      && this.normalizeSectionForComparison(candidate.sectionValue) === normalizedSection
      && this.normalizeTeachingTypeForComparison(candidate) === normalizedTeachingType);
  }

  // Mirrors the backend SubjectScheduleGroup.normalize_section_value so the
  // client-side duplicate check matches what the unique index enforces.
  private normalizeSectionForComparison(value: string): string {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? String(parseInt(trimmed, 10)) : trimmed;
  }

  private normalizeTeachingTypeForComparison(section: SubjectSection): string {
    return String(section.teachingTypeId ?? this.getTeachingTypeIdByLabel(section.teachingType) ?? '').trim();
  }

  // Direct-create payload for a brand-new section: same shape as
  // buildRegularSubmitApprovalPayload but without requiring teacher assignments
  // and without a `status` (the backend defaults the group to draft — no approval).
  private buildNewSectionPayload(section: SubjectSection): SubjectScheduleGroupCreatePayload | null {
    if (section.academicTermId == null || section.subjectId == null || this.isBlank(section.sectionValue)) {
      return null;
    }

    const teachingTimes = this.buildRegularSubjectTeachingTimes(section);

    if (teachingTimes.length === 0) {
      return null;
    }

    const isWorkloadSplit = section.workloadCalculation !== 'ไม่แบ่งภาระงาน';

    return {
      subject_schedule_group: {
        academic_term_id: section.academicTermId,
        subject_id: section.subjectId,
        ...(section.curriculumTypeId == null ? {} : { curriculum_type_id: section.curriculumTypeId }),
        created_by_id: this.authService.currentUser()?.user.id ?? null,
        section: section.sectionValue,
        sections: [{
          section: section.sectionValue,
          is_workload_split: isWorkloadSplit,
        }],
        replace_slots: true,
        study_mode_code: 'REG',
        teaching_type_id: this.getTeachingTypeId(section),
        room_name: this.displayOptionalText(section.room) || undefined,
        is_workload_split: isWorkloadSplit,
        student_count: this.toNumber(section.studentCount),
        teaching_times: teachingTimes,
      },
    };
  }

  private shiftIndexRecord<T>(record: Record<number, T>, removedIndex: number): Record<number, T> {
    const result: Record<number, T> = {};

    Object.entries(record).forEach(([key, value]) => {
      const index = Number(key);

      if (index < removedIndex) {
        result[index] = value;
      } else if (index > removedIndex) {
        result[index - 1] = value;
      }
    });

    return result;
  }

  private hasSelectedTeachingDatesForSection(section: SubjectSection): boolean {
    if (this.isRegularSection(section)) {
      return this.countSelectedAssignments(this.buildRegularSubjectTeachingTimes(section)) > 0;
    }

    return section.teachers.some((teacher) => this.isTeacherReadyForApproval(section, teacher));
  }

  private countSelectedAssignments(teachingTimes: SubjectScheduleGroupTeachingTime[]): number {
    return teachingTimes.reduce((total, teachingTime) => (
      total + (teachingTime.selected_by?.length ?? 0)
    ), 0);
  }

  private buildRegularSubjectAssignment(
    teacher: SubjectTeacherRow,
    section: string,
    teachingDay: SubjectTeachingDay,
    dayOfWeek: number,
    startTime: string,
    endTime: string,
  ): SubjectScheduleGroupAssignment | null {
    const scheduleSubmissionId = this.getScheduleSubmissionIdForTeacher(teacher);
    const selectedTeachingDates = this.getSelectedTeachingDatesForTime(
      teacher.teachingRanges,
      dayOfWeek,
      startTime,
      endTime
    );

    if (scheduleSubmissionId == null || selectedTeachingDates.length === 0) {
      return null;
    }

    return {
      schedule_submission_id: scheduleSubmissionId,
      section,
      status: 'submitted',
      selected_teaching_dates: selectedTeachingDates.map((selectedDate) => ({
        ...selectedDate,
        ...(teachingDay.subjectScheduleId == null ? {} : { subject_schedule_id: teachingDay.subjectScheduleId }),
        ...(teachingDay.teachingTimeKey == null ? {} : { teaching_time_key: teachingDay.teachingTimeKey }),
      })),
    };
  }

  private buildProjectResearchAssignments(section: SubjectSection): SubjectScheduleGroupAssignment[] {
    return section.teachers
      .map((teacher): SubjectScheduleGroupAssignment | null => {
        const scheduleSubmissionId = this.getScheduleSubmissionIdForTeacher(teacher);
        const teachingTimes = this.normalizeProjectResearchTeachingTimesForPayload(
          teacher.projectTeachingTimes ?? []
        );

        if (scheduleSubmissionId == null || teachingTimes.length === 0) {
          return null;
        }

        return {
          ...(teacher.userId == null ? {} : { user_id: teacher.userId }),
          schedule_submission_id: scheduleSubmissionId,
          section: section.sectionValue,
          status: 'submitted',
          description: teacher.description ?? '',
          primary_advisor_student_count: teacher.primaryAdvisorStudentCount ?? 0,
          co_advisor_student_count: teacher.coAdvisorStudentCount ?? 0,
          show_in_timetable: teacher.showInTimetable === true,
          teaching_times: teachingTimes,
        };
      })
      .filter((assignment): assignment is SubjectScheduleGroupAssignment => assignment != null);
  }

  private normalizeProjectResearchTeachingTimesForPayload(
    teachingTimes: readonly (SubjectScheduleGroupProjectTeachingTime | SectionTeacherProjectTeachingTime)[],
  ): SubjectScheduleGroupProjectTeachingTime[] {
    const normalized = teachingTimes
      .map((time): SubjectScheduleGroupProjectTeachingTime | null => {
        const timeRecord = time as unknown as Record<string, unknown>;
        const dayOfWeek = 'day_of_week' in time
          ? Number(time.day_of_week)
          : this.getDayOfWeek(time.teachingDay);
        const startTime = this.displayOptionalText(timeRecord['start_time'] ?? timeRecord['startTime']);
        const endTime = this.displayOptionalText(timeRecord['end_time'] ?? timeRecord['endTime']);

        if (dayOfWeek == null || !Number.isFinite(dayOfWeek) || !startTime || !endTime) {
          return null;
        }

        return {
          day_of_week: dayOfWeek,
          start_time: startTime,
          end_time: endTime,
        };
      })
      .filter((time): time is SubjectScheduleGroupProjectTeachingTime => time != null);

    return sortProjectTeachingTimes(normalized.map((time) => ({
      teachingDay: this.getThaiDayName(time.day_of_week),
      startTime: time.start_time,
      endTime: time.end_time,
    }))).map((time) => ({
      day_of_week: this.getDayOfWeek(time.teachingDay) ?? 0,
      start_time: time.startTime ?? '',
      end_time: time.endTime ?? '',
    }));
  }

  private getSelectedTeachingDatesForTime(
    teachingRange: unknown,
    dayOfWeek: number,
    startTime: string,
    endTime: string,
  ): { date: string }[] {
    return Array.from(new Set(this.normalizeTeachingRangeSelection(teachingRange)
      .filter((range) => teachingRangeMatchesTime(range, dayOfWeek, startTime, endTime))
      .map((range) => parseTeachingRangeIsoDate(range))
      .filter((date): date is NonNullable<ReturnType<typeof parseTeachingRangeIsoDate>> => date != null)
      .map((date) => date.date)))
      .map((date) => ({ date }));
  }

  private getScheduleSubmissionIdForTeacher(teacher: SubjectTeacherRow): number | null {
    if (teacher.scheduleSubmissionId != null) {
      return teacher.scheduleSubmissionId;
    }

    if (teacher.userId == null) {
      return null;
    }

    return this.teacherOptions.find((option) => option.id === teacher.userId)?.scheduleSubmissionId ?? null;
  }

  private getTeachingTypeId(section: SubjectSection): number | null {
    if (section.teachingTypeId != null) {
      return section.teachingTypeId;
    }

    return this.getTeachingTypeIdByLabel(section.teachingType);
  }

  private getTeachingTypeIdByLabel(value: unknown): number | null {
    const label = this.displayOptionalText(value);
    return this.teachingTypeOptions.find((option) => option.label === label)?.id ?? null;
  }

  private getDayOfWeek(value: unknown): number | null {
    const day = this.displayOptionalText(value);

    if (!day) {
      return null;
    }

    return this.thaiDayIndexes[day] ?? null;
  }

  private markSectionApprovalSubmitted(
    section: SubjectSection,
    response: SubjectScheduleGroupCreateResponse | ProjectResearchSubjectDetail,
  ): void {
    const responseStatuses = this.getResponseAssignmentStatuses(response);
    const status = responseStatuses.length > 0
      ? this.mapSectionAggregatedStatus(
        responseStatuses.map((item) => item.status),
        this.isRegularSection(section) && section.workloadCalculation === 'ไม่แบ่งภาระงาน'
          ? 'independent'
          : 'shared',
        this.getResponseStatus(response)
      )
      : this.mapTeachingApprovalStatus(this.getResponseStatus(response));

    section.rawStatus = 'submitted';
    section.status = status.status;
    section.statusLabel = status.label;
    section.validationSubmitted = false;
    section.teachers = section.teachers.map((teacher, index) => ({
      ...teacher,
      index: index + 1,
      ...this.getResponseTeacherStatusPatch(teacher, responseStatuses),
    }));

    if (this.subject != null) {
      const overallStatus = this.getOverallStatus(this.subject, this.subject.sections);
      this.subject.status = overallStatus.status;
      this.subject.statusLabel = overallStatus.label;
    }
    this.refreshSharedTeacherRows();
  }

  private shouldShowNoChangesWarning(
    section: SubjectSection,
    response: SubjectScheduleGroupCreateResponse | ProjectResearchSubjectDetail,
  ): boolean {
    return (response as SubjectScheduleGroupCreateResponse).no_changes === true &&
      this.isRevisionLikeSection(section);
  }

  private isRevisionLikeSection(section: SubjectSection): boolean {
    const statuses = [
      section.rawStatus,
      section.status,
      ...section.teachers.flatMap((teacher) => [
        teacher.approvalStatusType,
        teacher.approvalStatus,
      ]),
    ]
      .map((status) => normalizeTeachingApprovalStatus(status))
      .filter((status) => status !== '');

    return statuses.some((status) => (
      status === 'revision_requested' ||
      status === 'special_revision_requested' ||
      status === 'sent_back' ||
      status === 'special_sent_back' ||
      status.includes('ส่งกลับแก้ไข')
    ));
  }

  private getResponseTeacherStatusPatch(
    teacher: SubjectTeacherRow,
    statuses: { scheduleSubmissionId: number | null; status: string; comment: string }[],
  ): Pick<SubjectTeacherRow, 'approvalStatus' | 'approvalStatusType' | 'returnReason'> {
    const responseStatus = statuses.find((item) => (
      item.scheduleSubmissionId != null &&
      item.scheduleSubmissionId === teacher.scheduleSubmissionId
    ));

    if (responseStatus == null) {
      return {
        approvalStatus: teacher.approvalStatus,
        approvalStatusType: teacher.approvalStatusType,
        returnReason: teacher.returnReason,
      };
    }

    const status = this.mapTeachingApprovalStatus(responseStatus.status);

    return {
      approvalStatus: status.label,
      approvalStatusType: status.status,
      returnReason: responseStatus.comment,
    };
  }

  private getResponseAssignmentStatuses(
    response: SubjectScheduleGroupCreateResponse | ProjectResearchSubjectDetail,
  ): { scheduleSubmissionId: number | null; status: string; comment: string }[] {
    const subjectScheduleGroup = (response as SubjectScheduleGroupCreateResponse).subject_schedule_group;

    if (subjectScheduleGroup?.assignments != null) {
      return subjectScheduleGroup.assignments.map((assignment) => ({
        scheduleSubmissionId: assignment.schedule_submission_id ?? null,
        status: this.getAssignmentApprovalStatus(assignment),
        comment: this.getAssignmentReturnReason(assignment),
      }));
    }

    return ((response as ProjectResearchSubjectDetail).assignments ?? []).map((assignment) => ({
      scheduleSubmissionId: assignment.schedule_submission_id ?? null,
      status: this.getAssignmentApprovalStatus(assignment),
      comment: this.getAssignmentReturnReason(assignment),
    }));
  }

  private getResponseStatus(response: SubjectScheduleGroupCreateResponse | ProjectResearchSubjectDetail): string {
    const subjectScheduleResponse = response as SubjectScheduleGroupCreateResponse;

    if (subjectScheduleResponse.subject_schedule_group != null) {
      return this.displayOptionalText(subjectScheduleResponse.subject_schedule_group.status) || 'submitted';
    }

    return this.displayOptionalText((response as ProjectResearchSubjectDetail).status) || 'submitted';
  }

  private reloadCurrentSubjectDetails(onComplete?: () => void): void {
    if (this.subject == null) {
      onComplete?.();
      return;
    }

    this.loadSubjectDetails(this.subject.id, this.subject, onComplete);
  }

  private openTeacherManagementForSection(section: SubjectSection): void {
    this.isHydratingTeacherModal = true;
    this.activeTeacherSection = section;
    const workloadMode = section.workloadCalculation === 'ไม่แบ่งภาระงาน' ? 'notShared' : 'shared';
    this.teacherModalForm.controls.workloadMode.setValue(workloadMode, { emitEvent: false });
    const draftRows = section.teachers.length > 0
      ? section.teachers.map((teacher) => this.createTeacherDraftRow(teacher))
      : [this.createTeacherDraftRow()];
    this.teacherTeachingRangeOptions = this.buildTeacherTeachingRangeOptionsFromRows(draftRows);
    this.teacherModalErrorMessage = '';
    this.resetTeacherDraftRows(draftRows);
    this.isHydratingTeacherModal = false;
    this.loadTeacherTeachingRangeOptions(section);

    setTimeout(() => this.teacherManagementModal?.open());
  }

  private closeTeacherModal(): void {
    this.teacherManagementModal?.close();
  }

  private createTeacherDraftRow(row?: SubjectTeacherRow): TeacherDraftRow {
    const isUnsaved = row?.isUnsaved ?? row == null;

    return {
      userId: row?.userId ?? null,
      department: row?.department ?? '-',
      teachingRange: this.normalizeTeachingRangeSelection(row?.teachingRanges ?? row?.teachingDates ?? []),
      statusLabel: isUnsaved ? 'แบบร่าง' : row?.approvalStatus ?? 'รอดำเนินการ',
      statusType: isUnsaved ? 'draft' : row?.approvalStatusType ?? 'pending',
      statusCode: isUnsaved ? 'draft' : row?.approvalStatusCode ?? row?.approvalStatus ?? '',
      isUnsaved,
    };
  }

  private createTeacherDraftForm(row?: TeacherDraftRow): TeacherDraftForm {
    return new FormGroup({
      userId: new FormControl<number | null>(row?.userId ?? null),
      department: new FormControl(this.displayValue(row?.department), { nonNullable: true }),
      teachingRange: new FormControl(this.normalizeTeachingRangeSelection(row?.teachingRange ?? []), {
        nonNullable: true,
      }),
      statusLabel: new FormControl(row?.statusLabel ?? 'แบบร่าง', { nonNullable: true }),
      statusType: new FormControl(row?.statusType ?? 'draft', { nonNullable: true }),
      statusCode: new FormControl(row?.statusCode ?? 'draft', { nonNullable: true }),
      isUnsaved: new FormControl(row?.isUnsaved ?? true, { nonNullable: true }),
    });
  }

  private resetTeacherDraftRows(rows: TeacherDraftRow[]): void {
    this.teacherDraftRows.clear();
    rows.forEach((row) => this.teacherDraftRows.push(this.createTeacherDraftForm(row)));
    this.syncTeacherModalControlStates();
  }

  private getTeacherDraftRowsValue(): TeacherDraftRow[] {
    return this.teacherDraftRows.controls.map((row) => ({
      userId: row.get('userId')?.value ?? null,
      department: this.displayValue(row.get('department')?.value),
      teachingRange: this.normalizeTeachingRangeSelection(row.get('teachingRange')?.value),
      statusLabel: this.displayOptionalText(row.get('statusLabel')?.value) || 'แบบร่าง',
      statusType: this.displayOptionalText(row.get('statusType')?.value) as SubjectStatus || 'draft',
      statusCode: this.displayOptionalText(row.get('statusCode')?.value),
      isUnsaved: row.get('isUnsaved')?.value !== false,
    }));
  }

  private isTeacherDraftRowEditable(index: number): boolean {
    const row = this.teacherDraftRows.at(index);

    if (row == null) {
      return false;
    }

    return row.get('isUnsaved')?.value !== false || isTeacherModalRowEditableStatus(
      row.get('statusCode')?.value || row.get('statusLabel')?.value
    );
  }

  private isTeacherDraftRowApprovedByHead(index: number): boolean {
    const row = this.teacherDraftRows.at(index);

    if (row == null || row.get('isUnsaved')?.value !== false) {
      return false;
    }

    return isAssignmentApprovedByHead(
      row.get('statusCode')?.value || row.get('statusLabel')?.value
    );
  }

  private syncTeacherModalControlStates(): void {
    this.teacherDraftRows.controls.forEach((row, index) => {
      const isEditable = this.isTeacherDraftRowEditable(index);

      ['userId', 'teachingRange'].forEach((controlName) => {
        const control = row.get(controlName);

        if (isEditable) {
          control?.enable({ emitEvent: false });
        } else {
          control?.disable({ emitEvent: false });
        }
      });
    });

    if (this.shouldDisableWorkloadModeChange()) {
      this.teacherModalForm.controls.workloadMode.disable({ emitEvent: false });
    } else {
      this.teacherModalForm.controls.workloadMode.enable({ emitEvent: false });
    }
  }

  private isProjectTeacherEditableAt(sectionIndex: number, teacherIndex: number): boolean {
    const row = this.sectionTeacherRows[sectionIndex]?.[teacherIndex];

    if (row == null) {
      return false;
    }

    return isEditableAssignmentStatus(row.statusCode ?? row.status);
  }

  private getSelectedTeacherWorkloadMode(): TeacherWorkloadMode {
    return this.teacherModalForm.controls.workloadMode.value === 'notShared' ? 'notShared' : 'shared';
  }

  private hasTeacherDraftRowWithoutTeachingRange(): boolean {
    return this.teacherDraftRows.controls.some((row) => (
      row.get('userId')?.value != null &&
      this.normalizeTeachingRangeSelection(row.get('teachingRange')?.value).length === 0
    ));
  }

  private selectedTeachingDateMatchesTime(
    selectedDate: SubjectScheduleTeachingDateDetail,
    teachingTime: SubjectTeachingDay,
  ): boolean {
    const selectedSubjectScheduleId = this.toOptionalNumber(selectedDate.subject_schedule_id);

    if (selectedSubjectScheduleId != null && teachingTime.subjectScheduleId != null) {
      return selectedSubjectScheduleId === teachingTime.subjectScheduleId;
    }

    const selectedTeachingTimeKey = this.displayOptionalText(selectedDate.teaching_time_key);

    if (selectedTeachingTimeKey !== '' && teachingTime.teachingTimeKey != null) {
      return selectedTeachingTimeKey === teachingTime.teachingTimeKey;
    }

    const selectedDayOfWeek = this.getWeekdayFromDate(selectedDate.date) ?? Number(selectedDate.day_of_week);
    const teachingDayOfWeek = this.getDayOfWeek(teachingTime.day);

    return teachingDayOfWeek != null
      && selectedDayOfWeek === teachingDayOfWeek
      && this.formatTime(selectedDate.start_time) === this.formatTime(teachingTime.startTime)
      && this.formatTime(selectedDate.end_time) === this.formatTime(teachingTime.endTime);
  }

  private filterSelectedTeachingDatesForRanges(
    selectedDates: SubjectScheduleTeachingDateDetail[],
    teachingRanges: string[],
  ): SubjectScheduleTeachingDateDetail[] {
    const selectedRanges = teachingRanges
      .map((range) => parseTeachingRangeIsoDate(range))
      .filter((range): range is NonNullable<ReturnType<typeof parseTeachingRangeIsoDate>> => range != null);

    return selectedDates.filter((selectedDate) => {
      const date = this.displayOptionalText(selectedDate.date);
      const startTime = this.formatTime(selectedDate.start_time);
      const endTime = this.formatTime(selectedDate.end_time);

      return selectedRanges.some((range) => (
        range.date === date
        && (
          range.startTime == null
          || range.endTime == null
          || startTime === '-'
          || endTime === '-'
          || (range.startTime === startTime && range.endTime === endTime)
        )
      ));
    });
  }

  private mapTeacherDraftToRow(row: TeacherDraftRow, index: number): SubjectTeacherRow {
    const teacher = this.teacherOptions.find((option) => option.id === row.userId);
    const existingTeacher = this.activeTeacherSection?.teachers.find((item) => item.userId === row.userId);
    const teachingRanges = this.normalizeTeachingRangeSelection(row.teachingRange);

    return {
      index: index + 1,
      userId: row.userId,
      scheduleSubmissionId: existingTeacher?.scheduleSubmissionId ?? teacher?.scheduleSubmissionId ?? null,
      isUnsaved: row.isUnsaved,
      fullName: this.displayValue(teacher?.fullName),
      department: this.displayValue(teacher?.department ?? row.department),
      teachingDates: teachingRanges,
      teachingRanges,
      selectedTeachingDates: this.filterSelectedTeachingDatesForRanges(
        existingTeacher?.selectedTeachingDates ?? [],
        teachingRanges,
      ),
      teachingHours: calculateRegularTeacherWorkloadHours(
        teachingRanges,
        this.getActiveTeacherTeachingTimes(),
        this.activeTeacherSection?.workloadHours,
        this.getSelectedTeacherWorkloadMode()
      ),
      updatedAt: row.isUnsaved ? '-' : existingTeacher?.updatedAt ?? '-',
      updatedBy: row.isUnsaved ? '-' : existingTeacher?.updatedBy ?? '-',
      approvalStatus: row.isUnsaved
        ? 'แบบร่าง'
        : existingTeacher?.approvalStatus ?? row.statusLabel ?? 'รอดำเนินการ',
      approvalStatusType: row.isUnsaved
        ? 'draft'
        : existingTeacher?.approvalStatusType ?? row.statusType ?? 'pending',
      approvalStatusCode: row.isUnsaved
        ? 'draft'
        : existingTeacher?.approvalStatusCode ?? row.statusCode,
      returnReason: row.isUnsaved ? '' : existingTeacher?.returnReason ?? '',
    };
  }

  private mapUserToTeacherOption(
    user: UserManagementModel,
    scheduleSubmissionUsersByUserId: Partial<Record<number, ScheduleSubmissionUserOption>>,
  ): TeacherOption {
    const department = user.department as UserManagementModel['department'] | null;

    return {
      id: user.id,
      scheduleSubmissionId: scheduleSubmissionUsersByUserId[user.id]?.schedule_submission_id ?? null,
      fullName: this.displayValue(user.full_name),
      department: this.displayValue(department?.name),
    };
  }

  private buildTeacherTeachingRangeOptions(section: SubjectSection): TeachingRangeOption[] {
    return buildTeachingRangeOptions({
      teachingDays: section.teachingDays.map((teachingDay) => teachingDay.day),
      teachingTimes: section.teachingDays.map((teachingDay) => ({
        teachingDay: teachingDay.day,
        startTime: teachingDay.startTime,
        endTime: teachingDay.endTime,
      })),
      studyStartDate: this.academicTermStartDate,
      studyEndDate: this.academicTermEndDate,
      midtermStartDate: this.academicTermMidtermStartDate,
      midtermEndDate: this.academicTermMidtermEndDate,
      finalStartDate: this.academicTermFinalStartDate,
      finalEndDate: this.academicTermFinalEndDate,
      fallbackRanges: section.teachers.flatMap((teacher) => teacher.teachingRanges),
    });
  }

  private buildTeacherTeachingRangeOptionsFromRows(rows: TeacherDraftRow[]): TeachingRangeOption[] {
    return this.mergeTeachingRangeOptions(
      rows.flatMap((row) => this.normalizeTeachingRangeSelection(row.teachingRange))
        .map((label) => ({ value: label, label })),
    );
  }

  private loadTeacherTeachingRangeOptions(section: SubjectSection): void {
    const termName = this.displayOptionalText(this.subject?.academicTerm);
    const year = this.toChristianYear(this.subject?.year);

    if (!termName || year == null) {
      // buildTeacherTeachingRangeOptions() already folds in this section's saved
      // teaching ranges as its own exam-aware fallback, so there is nothing left
      // to merge here — merging in a separately-computed, unfiltered fallback
      // would resurrect exam-window dates that were correctly excluded above.
      this.teacherTeachingRangeOptions = this.buildTeacherTeachingRangeOptions(section);
      this.normalizeTeacherDraftTeachingRanges();
      this.requestViewUpdate();
      return;
    }

    this.studyPeriodService.getAcademicTerms({
      summary: true,
      name: termName,
      year,
      page: 1,
      limit: 1,
    }).pipe(
      take(1),
      catchError(() => of([] as AcademicTermModel[])),
    ).subscribe((terms) => {
      if (terms.length > 0) {
        const academicTerm = terms[0];

        this.academicTermStartDate = this.displayOptionalText(academicTerm.study_start_date);
        this.academicTermEndDate = this.displayOptionalText(academicTerm.study_end_date);
        this.academicTermMidtermStartDate = this.displayOptionalText(academicTerm.midterm_start_date);
        this.academicTermMidtermEndDate = this.displayOptionalText(academicTerm.midterm_end_date);
        this.academicTermFinalStartDate = this.displayOptionalText(academicTerm.final_start_date);
        this.academicTermFinalEndDate = this.displayOptionalText(academicTerm.final_end_date);
      }

      if (this.activeTeacherSection === section) {
        this.teacherTeachingRangeOptions = this.buildTeacherTeachingRangeOptions(section);
        this.normalizeTeacherDraftTeachingRanges();
        this.requestViewUpdate();
      }
    });
  }

  private mergeTeachingRangeOptions(...optionGroups: TeachingRangeOption[][]): TeachingRangeOption[] {
    return mergeTeachingRangeOptions(...optionGroups);
  }

  private normalizeTeacherDraftTeachingRanges(): void {
    this.teacherDraftRows.controls.forEach((row) => {
      row.controls.teachingRange.setValue(
        normalizeTeachingRangeValuesToOptions(
          row.controls.teachingRange.value,
          this.teacherTeachingRangeOptions
        ),
        { emitEvent: false }
      );
    });
  }

  private normalizeTeachingRangeSelection(value: unknown): string[] {
    const ranges = Array.isArray(value) ? value : [value];

    return ranges
      .flatMap((range) => this.splitTeachingRangeSelection(this.getTeachingRangeSelectionText(range)))
      .filter(Boolean);
  }

  private getTeachingRangeSelectionText(value: unknown): string {
    if (value != null && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      return this.toTeachingRangeText(record['value']) || this.toTeachingRangeText(record['label']);
    }

    return this.toTeachingRangeText(value);
  }

  private toTeachingRangeText(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value).trim();
    }

    return '';
  }

  private splitTeachingRangeSelection(range: string): string[] {
    if (range === '' || range === '-') {
      return [];
    }

    return range
      .split(/,\s*\n|\n|,\s*(?=วัน)/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private loadSubjectDetails(
    subjectId: number,
    fallbackSubject: SubjectDetail,
    onComplete?: () => void,
  ): void {
    const requestedTypes = this.resolveRequestedTypes();
    const hasSubjectSchedule = requestedTypes.includes('subject_schedule');
    const hasProjectResearch = requestedTypes.includes('project_research_subject');

    const subjectScheduleRequest = hasSubjectSchedule
      ? this.subjectScheduleService.getSubjectScheduleGroupDetailsBySubjectId(subjectId).pipe(
        take(1),
        map((data) => ({ data, failed: false })),
        catchError(() => of({ data: [] as SubjectScheduleGroupDetail[], failed: true })),
      )
      : of({ data: [] as SubjectScheduleGroupDetail[], failed: false });

    const projectResearchRequest = hasProjectResearch
      ? this.subjectScheduleService.getProjectResearchSubjectsBySubjectId(subjectId).pipe(
        take(1),
        map((data) => ({ data, failed: false })),
        catchError(() => of({ data: [] as ProjectResearchSubjectDetail[], failed: true })),
      )
      : of({ data: [] as ProjectResearchSubjectDetail[], failed: false });

    this.loading = true;
    this.errorMessage = '';
    this.requestViewUpdate();

    forkJoin({
      subjectSchedules: subjectScheduleRequest,
      projectResearchSubjects: projectResearchRequest,
    }).pipe(
      switchMap((result) => {
        const academicTermId = this.findAcademicTermId(
          result.subjectSchedules.data,
          result.projectResearchSubjects.data,
        );

        const scheduleSubmissionUsersParams: ScheduleSubmissionUsersParams = {
          academic_term_id: academicTermId,
        };

        if (this.correctionRequestId != null) {
          scheduleSubmissionUsersParams.include_special_correction = true;
        }

        const scheduleSubmissionUsersRequest = academicTermId == null
          ? of([] as ScheduleSubmissionUserOption[])
          : this.subjectScheduleService.getScheduleSubmissionUsers(scheduleSubmissionUsersParams).pipe(
            take(1),
            map((response) => response.data),
            catchError(() => of([] as ScheduleSubmissionUserOption[])),
          );
        const teacherUsersRequest = this.subjectScheduleService.getUsersRolseTeacherList({datatable : false}).pipe(
          take(1),
          map((response) => response.data),
          catchError(() => of([] as UserManagementModel[])),
        );

        return forkJoin({
          scheduleSubmissionUsers: scheduleSubmissionUsersRequest,
          teacherUsers: teacherUsersRequest,
        }).pipe(
          map(({ scheduleSubmissionUsers, teacherUsers }) => ({
            ...result,
            scheduleSubmissionUsers,
            teacherUsers,
          })),
        );
      }),
      finalize(() => {
        this.loading = false;
        this.requestViewUpdate();
      }),
    ).subscribe(({ subjectSchedules, projectResearchSubjects, scheduleSubmissionUsers, teacherUsers }) => {
      const scheduleSubmissionUsersByUserId = this.getScheduleSubmissionUsersByUserId(scheduleSubmissionUsers);

      this.teacherOptions = this.mergeScheduleSubmissionUsersIntoTeacherOptions(
        teacherUsers.map((user) => this.mapUserToTeacherOption(user, scheduleSubmissionUsersByUserId)),
        scheduleSubmissionUsers,
      );
      const sections = [
        ...subjectSchedules.data.map((group) => this.mapSubjectScheduleSection(group, scheduleSubmissionUsers)),
        ...projectResearchSubjects.data.map((record) => this.mapProjectResearchSection(record, scheduleSubmissionUsers)),
      ].sort((a, b) => this.compareSectionValues(a.sectionValue, b.sectionValue));

      this.subject = this.buildSubjectDetail(
        fallbackSubject,
        subjectSchedules.data,
        projectResearchSubjects.data,
        sections,
      );
      this.hydrateSharedView();
      this.openPendingTeacherModalAfterReload();

      if (subjectSchedules.failed || projectResearchSubjects.failed) {
        this.errorMessage = 'ไม่สามารถโหลดข้อมูลบางส่วนได้';
      }

      this.resolveActiveCorrectionRequestForSections(sections);
      this.requestViewUpdate();
      onComplete?.();
    });
  }

  private openPendingTeacherModalAfterReload(): void {
    const sectionValue = this.pendingOpenTeacherModalSectionValue;

    if (sectionValue == null) {
      return;
    }

    this.pendingOpenTeacherModalSectionValue = null;
    const matchedIndex = this.subject?.sections.findIndex((section) => section.sectionValue === sectionValue) ?? -1;

    if (matchedIndex >= 0) {
      this.onManageTeachersByIndex(matchedIndex);
    }
  }

  private mergeScheduleSubmissionUsersIntoTeacherOptions(
    teacherOptions: TeacherOption[],
    scheduleSubmissionUsers: ScheduleSubmissionUserOption[],
  ): TeacherOption[] {
    const optionsByUserId = new Map(teacherOptions.map((option) => [option.id, option]));

    scheduleSubmissionUsers.forEach((user) => {
      const existingOption = optionsByUserId.get(user.user_id);
      const department = this.displayValue(user.department?.name_th ?? user.department?.name_en ?? user.department?.name);

      if (existingOption != null) {
        optionsByUserId.set(user.user_id, {
          ...existingOption,
          scheduleSubmissionId: existingOption.scheduleSubmissionId ?? user.schedule_submission_id,
          fullName: existingOption.fullName === '-' ? this.displayValue(user.userName) : existingOption.fullName,
          department: existingOption.department === '-' ? department : existingOption.department,
        });
        return;
      }

      optionsByUserId.set(user.user_id, {
        id: user.user_id,
        scheduleSubmissionId: user.schedule_submission_id,
        fullName: this.displayValue(user.userName),
        department,
      });
    });

    return Array.from(optionsByUserId.values());
  }

  private getScheduleSubmissionUsersByUserId(
    users: ScheduleSubmissionUserOption[],
  ): Partial<Record<number, ScheduleSubmissionUserOption>> {
    return users.reduce<Partial<Record<number, ScheduleSubmissionUserOption>>>((optionsByUserId, user) => {
      optionsByUserId[user.user_id] = user;
      return optionsByUserId;
    }, {});
  }

  private requestViewUpdate(): void {
    this.cdr.markForCheck();
  }

  private hydrateSharedView(): void {
    const subject = this.subject;

    if (subject == null) {
      return;
    }

    const subjectTypeValue = this.resolveSubjectTypeValue(subject);
    this.academicTermOptions = [subject.academicTerm];
    this.yearOptions = [subject.year];
    this.subjectOptions = [{
      id: subject.id,
      code: subject.code,
      name: subject.name,
      curriculum: subject.curriculum,
      curriculumTypeId: subject.sections[0]?.curriculumTypeId ?? null,
      courseunit: subject.creditDescription,
    }];
    this.subjectForm.patchValue({
      academicTerm: subject.academicTerm,
      year: subject.year,
      subjectType: subjectTypeValue,
      subjectCode: subject.id,
      subjectName: subject.name,
      curriculum: subject.curriculum,
      creditDescription: subject.creditDescription,
    }, { emitEvent: false });

    this.sectionForms = subject.sections.map((section) => this.createSharedSectionForm(section));
    this.updateSectionDuplicateErrors();
    this.existingTeachingTimeCounts = subject.sections.reduce<Record<number, number>>((counts, section, index) => {
      counts[index] = section.teachingDays.length;
      return counts;
    }, {});
    this.sectionTeacherRows = subject.sections.reduce<Record<number, SubjectDetailsSectionTeacherRow[]>>((rows, section, index) => {
      rows[index] = section.teachers.map((teacher) => this.toSharedTeacherRow(teacher));
      return rows;
    }, {});
    subject.sections.forEach((_section, index) => this.syncTeachingTimeControlStates(index));
  }

  private createSharedSectionForm(section: SubjectSection): FormGroup {
    const form = createSectionForm(this.fb, section.sectionType === 'project_research', this.digitsOnlyValidators, {
      sectionValue: section.sectionValue,
      validateWorkloadHours: false,
    });
    const teachingTimes = form.get('teachingTimes') as FormArray<FormGroup>;

    teachingTimes.clear();
    section.teachingDays.forEach((time) => {
      this.ensureTimeOption(time.startTime);
      this.ensureTimeOption(time.endTime);
      const timeForm = createTeachingTimeForm(this.fb, section.sectionType === 'project_research');
      timeForm.patchValue({
        teachingDay: time.day,
        startTime: time.startTime,
        endTime: time.endTime,
      }, { emitEvent: false });
      teachingTimes.push(timeForm);
    });

    if (teachingTimes.length === 0) {
      teachingTimes.push(createTeachingTimeForm(this.fb, section.sectionType === 'project_research'));
    }

    form.patchValue({
      section: section.sectionValue,
      teachingType: section.teachingTypeId,
      room: section.room === '-' ? '' : section.room,
      studentCount: section.studentCount === '-' ? '' : section.studentCount,
      workloadHours: section.workloadHours === '-' ? '' : section.workloadHours,
    }, { emitEvent: false });

    return form;
  }

  private createDraftRegularSection(): SubjectSection {
    const sourceSection = this.subject?.sections[0] ?? null;

    return {
      id: null,
      academicTermId: sourceSection?.academicTermId ?? null,
      subjectId: sourceSection?.subjectId ?? this.subject?.id ?? null,
      curriculumTypeId: sourceSection?.curriculumTypeId ?? null,
      teachingTypeId: null,
      sectionValue: '',
      title: 'Section : -',
      sourceOrigin: 'created',
      sectionType: 'regular',
      rawStatus: 'draft',
      teachingType: '',
      status: 'pending',
      statusLabel: 'รอดำเนินการ',
      expanded: true,
      room: '',
      studentCount: '',
      workloadHours: '0',
      teachingDays: [],
      workloadCalculation: sourceSection?.workloadCalculation ?? 'แบ่งภาระงาน',
      teachers: [],
    };
  }

  private createDraftProjectResearchSection(): SubjectSection {
    const sourceSection = this.subject?.sections.find((section) => section.sectionType === 'project_research')
      ?? this.subject?.sections[0]
      ?? null;

    return {
      id: null,
      academicTermId: sourceSection?.academicTermId ?? null,
      subjectId: sourceSection?.subjectId ?? this.subject?.id ?? null,
      curriculumTypeId: sourceSection?.curriculumTypeId ?? null,
      teachingTypeId: null,
      sectionValue: '',
      title: 'Section : -',
      sourceOrigin: 'created',
      sectionType: 'project_research',
      rawStatus: 'draft',
      teachingType: this.getProjectResearchStudyModeLabel(),
      status: 'pending',
      statusLabel: 'รอดำเนินการ',
      expanded: true,
      room: '-',
      studentCount: '0',
      workloadHours: '0',
      teachingDays: [],
      workloadCalculation: '-',
      teachers: [],
    };
  }

  private shouldAddProjectResearchSection(): boolean {
    const requestedTypes = this.resolveRequestedTypes();

    if (requestedTypes.length === 1) {
      return requestedTypes[0] === 'project_research_subject';
    }

    return this.subject?.sections.some((section) => section.sectionType === 'project_research') === true
      && this.subject.sections.every((section) => section.sectionType !== 'regular');
  }

  private requestPendingDelete(pendingDelete: SubjectDetailsPendingDelete): void {
    this.pendingDelete = pendingDelete;
    this.deleteItemWarningDialog?.open();
  }

  private syncTeachingTimeControlStates(sectionIndex: number): void {
    const teachingTimes = this.sectionForms[sectionIndex]?.get('teachingTimes') as FormArray<FormGroup> | undefined;

    teachingTimes?.controls.forEach((timeForm, timeIndex) => {
      const isUsed = this.isTeachingTimeUsedAt(sectionIndex, timeIndex);

      ['teachingDay', 'startTime', 'endTime'].forEach((controlName) => {
        const control = timeForm.get(controlName);

        if (isUsed) {
          control?.disable({ emitEvent: false });
        } else {
          control?.enable({ emitEvent: false });
        }
      });
    });
    teachingTimes?.updateValueAndValidity({ emitEvent: false });
  }

  private clearPendingDelete(): void {
    this.pendingDelete = null;
    this.deleteItemWarningDialog?.close();
  }

  private removeSectionTeachingTime(sectionIndex: number, timeIndex: number): void {
    const teachingTimes = this.sectionForms[sectionIndex]?.get('teachingTimes') as FormArray<FormGroup> | undefined;

    if (teachingTimes != null && timeIndex > 0 && timeIndex < teachingTimes.length) {
      teachingTimes.removeAt(timeIndex);
      this.subject?.sections[sectionIndex]?.teachingDays.splice(timeIndex, 1);
    }
  }

  private removeProjectTeachingTime(timeIndex: number): void {
    if (timeIndex > 0 && timeIndex < this.projectTeachingTimes.length) {
      this.projectTeachingTimes.removeAt(timeIndex);
      this.updateProjectWorkloadHours();
    }
  }

  private removeProjectTeacher(sectionIndex: number, teacherIndex: number): void {
    const rows = [...(this.sectionTeacherRows[sectionIndex] ?? [])];

    if (teacherIndex < 0 || teacherIndex >= rows.length) {
      return;
    }

    rows.splice(teacherIndex, 1);
    this.sectionTeacherRows[sectionIndex] = rows;
    this.syncProjectTeachers(sectionIndex);
  }

  private removeTeacherDraftRow(index: number): void {
    if (index < 0 || index >= this.teacherDraftRows.length) {
      return;
    }

    this.teacherDraftRows.removeAt(index);
    this.requestViewUpdate();
  }

  private isSharedSectionReadyForTeacherModal(sectionIndex: number): boolean {
    if (!this.hasSharedSectionAt(sectionIndex)) {
      return false;
    }

    const form = this.sectionForms[sectionIndex];
    const section = this.subject.sections[sectionIndex];
    const teachingTimes = this.getTeachingTimesFormAt(sectionIndex);

    teachingTimes.updateValueAndValidity({ emitEvent: false });
    form.updateValueAndValidity({ emitEvent: false });

    return form.valid && this.validateSection(section);
  }

  private markSharedSectionForTeacherModalValidation(sectionIndex: number): void {
    if (!this.hasSharedSectionAt(sectionIndex)) {
      return;
    }

    const form = this.sectionForms[sectionIndex];
    const teachingTimes = this.getTeachingTimesFormAt(sectionIndex);

    form.markAllAsTouched();
    teachingTimes.markAsDirty();
    teachingTimes.markAsTouched();
    teachingTimes.controls.forEach((time) => time.markAllAsTouched());
    teachingTimes.updateValueAndValidity({ emitEvent: false });
    form.updateValueAndValidity({ emitEvent: false });
  }

  private getTeachingTimesFormAt(sectionIndex: number): FormArray<FormGroup> {
    return this.sectionForms[sectionIndex].get('teachingTimes') as FormArray<FormGroup>;
  }

  private hasSharedSectionAt(sectionIndex: number): this is this & { subject: SubjectDetail } {
    return (
      this.subject != null
      && sectionIndex >= 0
      && sectionIndex < this.sectionForms.length
      && sectionIndex < this.subject.sections.length
    );
  }

  private syncSectionFromForm(sectionIndex: number): void {
    const section = this.subject?.sections[sectionIndex];
    const form = this.sectionForms[sectionIndex];

    if (section == null) {
      return;
    }

    const value = form.getRawValue();
    section.sectionValue = String(value.section ?? '');
    section.teachingTypeId = Number(value.teachingType) || null;
    section.teachingType = this.teachingTypeOptions.find((option) => option.id === section.teachingTypeId)?.label ?? '-';
    section.room = String(value.room ?? '');
    section.studentCount = String(value.studentCount ?? '');
    section.workloadHours = String(value.workloadHours ?? '');
    const existingTeachingDays = section.teachingDays;
    section.teachingDays = ((value.teachingTimes ?? []) as {
      teachingDay?: unknown;
      startTime?: unknown;
      endTime?: unknown;
    }[]).map((time, index) => {
      const existingTeachingDay = index < existingTeachingDays.length
        ? existingTeachingDays[index]
        : null;

      return {
        subjectScheduleId: existingTeachingDay?.subjectScheduleId ?? null,
        teachingTimeKey: existingTeachingDay?.teachingTimeKey ?? null,
        teachingTypeId: existingTeachingDay?.teachingTypeId ?? section.teachingTypeId ?? null,
        day: this.displayOptionalText(time.teachingDay),
        startTime: this.displayOptionalText(time.startTime),
        endTime: this.displayOptionalText(time.endTime),
        time: this.buildTeachingTimeLabel(time.startTime, time.endTime),
        isNew: existingTeachingDay == null,
      };
    });
  }

  private updateSectionDuplicateErrors(): void {
    updateSectionDuplicateErrors(this.sectionForms);
  }

  private openProjectTeacher(
    sectionIndex: number,
    teacherIndex: number | null = null,
    row?: SectionTeacherRow,
  ): void {
    if (!this.isSectionEditableAt(sectionIndex)) {
      return;
    }

    this.activeProjectSectionIndex = sectionIndex;
    this.editingProjectTeacherIndex = teacherIndex;
    this.projectTeacherSubmitted = false;
    this.projectTeacherForm.reset({
      userId: null,
      department: '',
      workloadHours: '0',
      primaryStudentCount: '',
      coStudentCount: '',
      description: '',
      showInSchedule: false,
    }, { emitEvent: false });
    this.projectTeachingTimes.clear();

    const teachingTimes = row?.projectTeachingTimes != null && row.projectTeachingTimes.length > 0
      ? sortProjectTeachingTimes(row.projectTeachingTimes)
      : [{ teachingDay: null, startTime: null, endTime: null }];

    teachingTimes.forEach((time) => {
      this.ensureTimeOption(time.startTime);
      this.ensureTimeOption(time.endTime);
      this.projectTeachingTimes.push(createProjectTeachingTimeForm(this.fb, time));
    });

    this.projectTeacherForm.patchValue({
      userId: row?.userId ?? null,
      department: this.getProjectTeacherDepartment(row),
      workloadHours: row?.workloadHours ?? '0',
      primaryStudentCount: row?.primaryAdvisorStudentCount == null ? '' : String(row.primaryAdvisorStudentCount),
      coStudentCount: row?.coAdvisorStudentCount == null ? '' : String(row.coAdvisorStudentCount),
      description: row?.description ?? '',
      showInSchedule: row?.showInTimetable === true,
    }, { emitEvent: false });
    this.projectTeachingTimes.updateValueAndValidity({ emitEvent: false });
    this.updateProjectWorkloadHours();
    this.projectTeacherModal?.open();
  }

  private closeProjectTeacherModal(): void {
    this.projectTeacherModal?.close();
    this.activeProjectSectionIndex = null;
    this.editingProjectTeacherIndex = null;
  }

  private getProjectTeachingTimeValues(): TeachingTimeValue[] {
    return this.projectTeachingTimes.controls.map((time) => ({
      teachingDay: time.get('teachingDay')?.value,
      startTime: time.get('startTime')?.value,
      endTime: time.get('endTime')?.value,
    }));
  }

  private syncProjectTeachers(sectionIndex: number): void {
    if (this.subject == null) {
      return;
    }

    const section = this.subject.sections[sectionIndex];

    if (section.sectionType !== 'project_research') {
      return;
    }

    section.teachers = (this.sectionTeacherRows[sectionIndex] ?? []).map((teacher, index) => ({
      index: index + 1,
      userId: teacher.userId,
      scheduleSubmissionId: teacher.scheduleSubmissionId
        ?? this.teacherOptions.find((option) => option.id === teacher.userId)?.scheduleSubmissionId
        ?? null,
      fullName: teacher.fullName,
      department: teacher.department,
      teachingDates: [],
      teachingRanges: [],
      teachingHours: teacher.workloadHours,
      updatedAt: teacher.updatedAt,
      updatedBy: teacher.updatedBy ?? '-',
      approvalStatus: teacher.status,
      approvalStatusType: (teacher.statusType as SubjectStatus | null | undefined) ?? 'draft',
      approvalStatusCode: teacher.statusCode ?? teacher.status,
      returnReason: teacher.returnReason,
      description: teacher.description ?? undefined,
      primaryAdvisorStudentCount: Number(teacher.primaryAdvisorStudentCount ?? 0),
      coAdvisorStudentCount: Number(teacher.coAdvisorStudentCount ?? 0),
      showInTimetable: teacher.showInTimetable === true,
      projectTeachingTimes: sortProjectTeachingTimes(teacher.projectTeachingTimes ?? []).map((time) => ({
        day_of_week: this.getDayOfWeek(time.teachingDay) ?? 0,
        start_time: time.startTime ?? '',
        end_time: time.endTime ?? '',
      })),
    }));
  }

  private getProjectTeacherDepartment(row?: SectionTeacherRow): string {
    const department = this.displayOptionalText(row?.department);

    if (department !== '') {
      return department;
    }

    return this.teacherOptions.find((option) => option.id === row?.userId)?.department ?? '';
  }

  private getProjectTeacherRangeText(time: SectionTeacherProjectTeachingTime): string {
    if (time.teachingDay == null) {
      return '';
    }

    return `${time.teachingDay} ${time.startTime} - ${time.endTime}`.trim();
  }

  private toSharedTeacherRow(teacher: SubjectTeacherRow): SubjectDetailsSectionTeacherRow {
    const projectTeachingTimes = sortProjectTeachingTimes(teacher.projectTeachingTimes?.map((time) => ({
      teachingDay: this.getDayLabel(time.day_of_week),
      startTime: time.start_time,
      endTime: time.end_time,
    })) ?? []);
    const projectTeachingRange = projectTeachingTimes.map((time) => this.getProjectTeacherRangeText(time))
      .filter((range) => range !== '');

    return {
      userId: teacher.userId ?? null,
      scheduleSubmissionId: teacher.scheduleSubmissionId ?? null,
      fullName: teacher.fullName,
      department: teacher.department,
      teachingRange: projectTeachingRange.length > 0
        ? projectTeachingRange.join(', ')
        : teacher.teachingRanges.join(', '),
      workloadHours: teacher.teachingHours,
      updatedAt: teacher.updatedAt,
      updatedBy: teacher.updatedBy,
      status: teacher.approvalStatus,
      statusType: teacher.approvalStatusType,
      statusCode: teacher.approvalStatusCode,
      returnReason: teacher.returnReason || '-',
      projectTeachingTimes,
      primaryAdvisorStudentCount: teacher.primaryAdvisorStudentCount,
      coAdvisorStudentCount: teacher.coAdvisorStudentCount,
      description: teacher.description,
      showInTimetable: teacher.showInTimetable,
    };
  }

  private resolveSubjectTypeValue(subject: SubjectDetail): number | string | null {
    const normalizedType = subject.subjectType.trim();

    if (normalizedType === '' || normalizedType === '-') {
      return null;
    }

    const exactOption = this.subjectTypeOptions.find((option) => option.label === normalizedType);

    if (exactOption != null) {
      return exactOption.id;
    }

    if (normalizedType.includes('/')) {
      return normalizedType;
    }

    return this.subjectTypeOptions.find((option) => normalizedType.includes(option.label))?.id ?? normalizedType;
  }

  private refreshSharedTeacherRows(): void {
    this.subject?.sections.forEach((section, index) => {
      this.sectionTeacherRows[index] = section.teachers.map((teacher) => this.toSharedTeacherRow(teacher));
    });
  }

  private getDayLabel(value: unknown): string | null {
    const dayIndex = Number(value);
    const entry = Object.entries(this.thaiDayIndexes).find(([, index]) => index === dayIndex);
    return entry?.[0] ?? null;
  }

  private navigateBack(): void {
    void this.router.navigate(['/subject-management']);
  }

  private getSubjectFromNavigationState(id: number): SubjectDetail | undefined {
    const row = window.history.state?.['subjectManagementRow'] as SubjectManagementRow | undefined;

    if (row == null || Number(row.id) !== id) {
      return undefined;
    }

    const status = this.mapSubjectManagementDetailMainStatus(
      row.source?.approval_status?.status_code ?? row.source?.status ?? row.status,
    );

    return {
      id,
      sourceOrigin: 'created',
      academicTerm: row.academicTerm,
      year: row.year,
      subjectType: this.getSubjectTypeFromRow(row),
      code: row.code,
      name: row.name,
      curriculum: row.curriculum,
      creditDescription: this.displayValue(row.source?.subject?.courseunit ?? row.source?.subject?.credit),
      status: status.status,
      statusLabel: status.label,
      sections: [],
    };
  }

  private createEmptySubject(id: number): SubjectDetail {
    return {
      id,
      sourceOrigin: 'created',
      academicTerm: '-',
      year: '-',
      subjectType: '-',
      code: '-',
      name: '-',
      curriculum: '-',
      creditDescription: '-',
      status: 'pending',
      statusLabel: 'รอดำเนินการ',
      sections: [],
    };
  }

  private buildSubjectDetail(
    fallbackSubject: SubjectDetail,
    subjectSchedules: SubjectScheduleGroupDetail[],
    projectResearchSubjects: ProjectResearchSubjectDetail[],
    sections: SubjectSection[],
  ): SubjectDetail {
    const firstRecord = this.findFirstDetailRecord(subjectSchedules, projectResearchSubjects);
    const status = this.getOverallStatus(fallbackSubject, sections);
    const academicTerm = firstRecord?.academic_term;
    const subject = firstRecord?.subject;
    this.academicTermStartDate = this.displayOptionalText(academicTerm?.study_start_date);
    this.academicTermEndDate = this.displayOptionalText(academicTerm?.study_end_date);

    return {
      ...fallbackSubject,
      academicTerm: this.displayValue(academicTerm?.name ?? fallbackSubject.academicTerm),
      year: this.displayYear(academicTerm?.year ?? fallbackSubject.year),
      subjectType: this.getSubjectTypeFromRecords(subjectSchedules, projectResearchSubjects, fallbackSubject.subjectType),
      code: this.displayValue(subject?.code ?? fallbackSubject.code),
      name: this.displayValue(subject?.name ?? fallbackSubject.name),
      curriculum: this.getCurriculumLabel(firstRecord, fallbackSubject.curriculum),
      creditDescription: this.getCreditDescriptionLabel(firstRecord, fallbackSubject.creditDescription),
      status: status.status,
      statusLabel: status.label,
      sections,
    };
  }

  private getCurriculumLabel(
    record: SubjectScheduleGroupDetail | ProjectResearchSubjectDetail | undefined,
    fallback: string,
  ): string {
    return this.displayValue(record?.curriculum_type?.name ?? record?.subject?.curriculum_type?.name ?? fallback);
  }

  private getCreditDescriptionLabel(
    record: SubjectScheduleGroupDetail | ProjectResearchSubjectDetail | undefined,
    fallback: string,
  ): string {
    return this.displayValue(record?.subject?.courseunit ?? record?.subject?.credit ?? fallback);
  }

  private findFirstDetailRecord(
    subjectSchedules: SubjectScheduleGroupDetail[],
    projectResearchSubjects: ProjectResearchSubjectDetail[],
  ): SubjectScheduleGroupDetail | ProjectResearchSubjectDetail | undefined {
    if (subjectSchedules.length > 0) {
      return subjectSchedules[0];
    }

    if (projectResearchSubjects.length > 0) {
      return projectResearchSubjects[0];
    }

    return undefined;
  }

  private mapSubjectScheduleSection(
    group: SubjectScheduleGroupDetail,
    users: ScheduleSubmissionUserOption[],
  ): SubjectSection {
    const assignmentStatuses = (group.assignments ?? []).map((assignment) => this.getAssignmentApprovalStatus(assignment));
    const rawStatus = this.getSectionApprovalStatus(group.assignments ?? [], group.status);
    const status = this.mapSectionAggregatedStatus(
      assignmentStatuses,
      group.is_workload_split === false ? 'independent' : 'shared',
      group.status,
    );
    const sectionValue = this.displayValue(group.section);
    const teachingTimes = this.mapRegularTeachingDays(group);
    const sectionWorkloadHours = this.displayNumber(group.workload_cap_hours ?? group.teaching_hours);
    const ids = this.regularSectionIds(group);

    return {
      id: Number(group.id),
      academicTermId: ids.academicTermId,
      subjectId: ids.subjectId,
      curriculumTypeId: ids.curriculumTypeId,
      teachingTypeId: group.teaching_type_id ?? null,
      sectionValue,
      title: `Section : ${sectionValue}`,
      sourceOrigin: 'created',
      sectionType: 'regular',
      rawStatus: this.normalizeStatus(rawStatus),
      teachingType: this.getTeachingTypeLabel(group.teaching_type_id),
      status: status.status,
      statusLabel: status.label,
      expanded: true,
      room: this.regularSectionRoom(group),
      studentCount: this.displayNumber(group.student_count),
      workloadHours: sectionWorkloadHours,
      teachingDays: teachingTimes,
      workloadCalculation: this.regularSectionWorkloadCalculation(group),
      teachers: (group.assignments ?? []).map((assignment, index) => this.mapRegularTeacherRow(
        assignment,
        users,
        index,
        sectionWorkloadHours,
        teachingTimes,
        group.is_workload_split === false ? 'notShared' : 'shared',
      )),
    };
  }

  private regularSectionIds(group: SubjectScheduleGroupDetail): {
    academicTermId: number | null;
    subjectId: number | null;
    curriculumTypeId: number | null;
  } {
    return {
      academicTermId: group.academic_term_id ?? group.academic_term?.id ?? null,
      subjectId: group.subject_id ?? group.subject?.id ?? null,
      curriculumTypeId: group.curriculum_type_id ?? group.curriculum_type?.id ?? group.subject?.curriculum_type?.id ?? null,
    };
  }

  private regularSectionRoom(group: SubjectScheduleGroupDetail): string {
    const teachingDateRoom = group.teaching_dates?.find((date) => this.displayValue(date.room_name) !== '-')?.room_name;

    return this.displayValue(group.room_name ?? teachingDateRoom);
  }

  private regularSectionWorkloadCalculation(group: SubjectScheduleGroupDetail): string {
    return group.is_workload_split === false ? 'ไม่แบ่งภาระงาน' : 'แบ่งภาระงาน';
  }

  private mapProjectResearchSection(
    record: ProjectResearchSubjectDetail,
    users: ScheduleSubmissionUserOption[],
  ): SubjectSection {
    const sectionValue = this.displayValue(record.section);
    const assignments = record.assignments ?? [];
    const assignmentStatuses = assignments.map((assignment) => this.getAssignmentApprovalStatus(assignment));
    const sectionApprovalStatus = this.getProjectSectionApprovalStatus(assignments, record.status);
    const status = this.mapSectionAggregatedStatus(assignmentStatuses, 'independent', record.status);
    const teachingDays = this.uniqueTeachingDays(assignments.flatMap((assignment) => assignment.teaching_times ?? []));

    return {
      id: Number(record.id),
      academicTermId: record.academic_term_id ?? record.academic_term?.id ?? null,
      subjectId: record.subject_id ?? record.subject?.id ?? null,
      curriculumTypeId: record.curriculum_type_id ?? record.curriculum_type?.id ?? record.subject?.curriculum_type?.id ?? null,
      sectionValue,
      title: `Section : ${sectionValue}`,
      sourceOrigin: 'created',
      sectionType: 'project_research',
      rawStatus: this.normalizeStatus(sectionApprovalStatus),
      teachingType: this.getStudyModeLabel(record.study_mode, (record as { study_mode_id?: unknown }).study_mode_id),
      status: status.status,
      statusLabel: status.label,
      expanded: true,
      room: '-',
      studentCount: this.displayNumber(this.sumProjectStudentCount(assignments)),
      workloadHours: this.displayNumber(this.sumProjectTeachingHours(assignments)),
      teachingDays,
      workloadCalculation: '-',
      teachers: assignments.map((assignment, index) => this.mapProjectTeacherRow(assignment, users, index)),
    };
  }

  private mapRegularTeacherRow(
    assignment: SubjectScheduleAssignmentDetail,
    users: ScheduleSubmissionUserOption[],
    index: number,
    sectionWorkloadHours: string,
    teachingTimes: SubjectTeachingDay[],
    workloadMode: TeacherWorkloadMode,
  ): SubjectTeacherRow {
    const user = users.find((item) => (
      item.user_id === assignment.user_id
      || item.schedule_submission_id === assignment.schedule_submission_id
    ));
    const teacherOption = this.teacherOptions.find((option) => option.id === (assignment.user_id ?? user?.user_id));
    const approvalStatusCode = this.getAssignmentApprovalStatus(assignment);
    const status = this.mapTeachingApprovalStatus(approvalStatusCode);
    const teachingRanges = this.formatTeachingDates(assignment.selected_teaching_dates ?? []);

    return {
      index: index + 1,
      userId: user?.user_id ?? assignment.user_id ?? null,
      scheduleSubmissionId: assignment.schedule_submission_id ?? user?.schedule_submission_id ?? null,
      fullName: this.displayValue(user?.userName ?? teacherOption?.fullName),
      department: this.displayValue(user?.department?.name ?? teacherOption?.department),
      teachingDates: teachingRanges,
      teachingRanges,
      selectedTeachingDates: assignment.selected_teaching_dates ?? [],
      teachingHours: calculateRegularTeacherWorkloadHours(
        teachingRanges,
        teachingTimes.map((time) => ({
          teachingDay: time.day,
          startTime: time.startTime,
          endTime: time.endTime,
        })),
        sectionWorkloadHours,
        workloadMode
      ),
      updatedAt: this.formatStatusUpdatedAt(assignment.status_updated_at),
      updatedBy: this.formatStatusUpdatedBy(assignment.status_updated_by),
      approvalStatus: status.label,
      approvalStatusType: status.status,
      approvalStatusCode,
      returnReason: this.getAssignmentReturnReason(assignment),
    };
  }

  private mapProjectTeacherRow(
    assignment: ProjectResearchAssignmentDetail,
    users: ScheduleSubmissionUserOption[],
    index: number,
  ): SubjectTeacherRow {
    const user = users.find((item) => (
      item.user_id === assignment.user_id
      || item.schedule_submission_id === assignment.schedule_submission_id
    ));
    const teacherOption = this.teacherOptions.find((option) => option.id === (assignment.user_id ?? user?.user_id));
    const approvalStatusCode = this.getAssignmentApprovalStatus(assignment);
    const status = this.mapTeachingApprovalStatus(approvalStatusCode);

    return {
      index: index + 1,
      userId: user?.user_id ?? assignment.user_id ?? null,
      scheduleSubmissionId: assignment.schedule_submission_id ?? user?.schedule_submission_id ?? null,
      fullName: this.displayValue(user?.userName ?? assignment.user?.full_name ?? teacherOption?.fullName),
      department: this.displayValue(user?.department?.name ?? teacherOption?.department),
      teachingDates: this.formatProjectTeachingTimes(assignment.teaching_times ?? []),
      teachingRanges: this.formatProjectTeachingTimes(assignment.teaching_times ?? []),
      teachingHours: this.displayNumber(this.sumTeachingTimeHours(assignment.teaching_times ?? [])),
      updatedAt: this.formatStatusUpdatedAt(assignment.status_updated_at),
      updatedBy: this.formatStatusUpdatedBy(assignment.status_updated_by),
      approvalStatus: status.label,
      approvalStatusType: status.status,
      approvalStatusCode,
      returnReason: this.getAssignmentReturnReason(assignment),
      description: this.displayOptionalText(assignment.description),
      primaryAdvisorStudentCount: this.toNumber(assignment.primary_advisor_student_count),
      coAdvisorStudentCount: this.toNumber(assignment.co_advisor_student_count),
      showInTimetable: assignment.show_in_timetable === true,
      projectTeachingTimes: this.mapProjectTeachingTimesForPayload(assignment.teaching_times ?? []),
    };
  }

  private resolveRequestedTypes(): SubjectDetailSourceType[] {
    const queryType = this.route.snapshot.queryParamMap.get('type');
    const queryTypes = this.route.snapshot.queryParamMap.get('types');
    const row = window.history.state?.['subjectManagementRow'] as SubjectManagementRow | undefined;
    const values = [
      queryType,
      ...(queryTypes?.split(',') ?? []),
      row?.source?.type,
      ...(row?.source?.types ?? []),
    ].map((value) => String(value ?? '').trim());
    const hasSubjectSchedule = values.includes('subject_schedule') || values.includes('mixed');
    const hasProjectResearch = values.includes('project_research_subject') || values.includes('mixed');
    const types: SubjectDetailSourceType[] = [];

    if (hasSubjectSchedule) {
      types.push('subject_schedule');
    }

    if (hasProjectResearch) {
      types.push('project_research_subject');
    }

    return types.length > 0 ? types : ['subject_schedule', 'project_research_subject'];
  }

  private getSubjectTypeFromRow(row: SubjectManagementRow): string {
    const studyModes = [
      row.source?.study_mode,
      ...(row.source?.study_modes ?? []),
    ].filter((mode): mode is StudyModeModel => mode != null);
    const labels = studyModes
      .map((mode) => this.getStudyModeLabel(mode, mode.id))
      .filter((label) => label !== '-');

    return labels.length > 0 ? labels.join(' / ') : '-';
  }

  private getSubjectTypeFromRecords(
    subjectSchedules: SubjectScheduleGroupDetail[],
    projectResearchSubjects: ProjectResearchSubjectDetail[],
    fallback: string,
  ): string {
    const regularLabels = subjectSchedules
      .map((record) => this.getStudyModeLabel(record.study_mode, (record as { study_mode_id?: unknown }).study_mode_id))
      .filter((label) => label !== '-');
    const projectLabels = projectResearchSubjects
      .map((record) => this.getStudyModeLabel(record.study_mode, (record as { study_mode_id?: unknown }).study_mode_id))
      .filter((label) => label !== '-');
    const labels = Array.from(new Set([
      ...regularLabels,
      ...projectLabels,
    ]));

    return labels.length > 0 ? labels.join(' / ') : fallback;
  }

  private getOverallStatus(
    fallbackSubject: SubjectDetail,
    sections: SubjectSection[],
  ): { status: SubjectStatus; label: string } {
    if (sections.length === 0) {
      return this.mapSubjectManagementDetailMainStatus(fallbackSubject.status);
    }

    return this.mapSectionAggregatedStatus(
      sections.map((section) => section.rawStatus),
      'shared',
      fallbackSubject.status,
    );
  }

  private findAcademicTermId(
    subjectSchedules: SubjectScheduleGroupDetail[],
    projectResearchSubjects: ProjectResearchSubjectDetail[],
  ): number | undefined {
    return subjectSchedules[0]?.academic_term_id
      ?? subjectSchedules[0]?.academic_term?.id
      ?? projectResearchSubjects[0]?.academic_term_id
      ?? projectResearchSubjects[0]?.academic_term?.id
      ?? undefined;
  }

  private mapRegularTeachingDays(group: SubjectScheduleGroupDetail): SubjectTeachingDay[] {
    const teachingTimes = group.teaching_times ?? [];

    if (teachingTimes.length > 0) {
      return this.uniqueTeachingDays(teachingTimes);
    }

    return this.uniqueTeachingDays(group.teaching_dates ?? []);
  }

  private uniqueTeachingDays(
    teachingTimes: (SubjectScheduleTeachingTimeDetail | ProjectResearchTeachingTimeDetail)[],
  ): SubjectTeachingDay[] {
    const seen = new Set<string>();

    return teachingTimes
      .map((time) => {
        const timeRecord = time as Record<string, unknown>;
        const startTime = this.formatTime(time.start_time);
        const endTime = this.formatTime(time.end_time);

        return {
          subjectScheduleId: this.toOptionalNumber(timeRecord['subject_schedule_id']),
          teachingTimeKey: this.displayOptionalText(timeRecord['key']) || null,
          teachingTypeId: this.toOptionalNumber(timeRecord['teaching_type_id']),
          day: this.getThaiDayName(time.day_of_week),
          startTime,
          endTime,
          time: this.buildTeachingTimeLabel(startTime, endTime),
          isNew: false,
        };
      })
      .filter((item) => item.day !== '-' && item.time !== '- - -')
      .filter((item) => {
        const key = `${item.day}|${item.time}`;

        if (seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      });
  }

  private formatTeachingDates(dates: SubjectScheduleTeachingDateDetail[]): string[] {
    const counters = new Map<number, number>();

    return dates
      .filter((date) => this.displayValue(date.date) !== '-')
      .sort((a, b) => this.compareTeachingDatesByCreateOrder(a, b))
      .map((date) => {
        const weekday = this.getWeekdayFromDate(date.date) ?? Number(date.day_of_week);
        const nextCount = (counters.get(weekday) ?? 0) + 1;
        const fallbackLabel = `${this.getThaiDayName(weekday)}ที่ ${nextCount}`;
        const sequenceDay = this.displayOptionalText(date.sequence_day);
        const timeLabel = this.getTeachingDateTimeLabel(date);
        const hasTime = sequenceDay ? /\(\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}\)/.test(sequenceDay) : false;
        const sequenceLabel = timeLabel === '' || hasTime
          ? sequenceDay || fallbackLabel
          : `${sequenceDay || fallbackLabel} (${timeLabel})`;

        counters.set(weekday, nextCount);
        return buildSelectedTeachingDateLabel(
          sequenceLabel,
          timeLabel === '' ? fallbackLabel : `${fallbackLabel} (${timeLabel})`,
          this.formatThaiDate(date.date),
        );
      });
  }

  private getTeachingDateTimeLabel(date: SubjectScheduleTeachingDateDetail): string {
    const startTime = this.formatTime(date.start_time);
    const endTime = this.formatTime(date.end_time);

    if (startTime === '-' || endTime === '-') {
      return '';
    }

    return `${startTime} - ${endTime}`;
  }

  private compareTeachingDatesByCreateOrder(
    current: SubjectScheduleTeachingDateDetail,
    next: SubjectScheduleTeachingDateDetail,
  ): number {
    const currentWeekday = this.getWeekdayFromDate(current.date) ?? Number(current.day_of_week);
    const nextWeekday = this.getWeekdayFromDate(next.date) ?? Number(next.day_of_week);
    const currentDayOrder = this.teachingDateSortOrder[currentWeekday] ?? Number.MAX_SAFE_INTEGER;
    const nextDayOrder = this.teachingDateSortOrder[nextWeekday] ?? Number.MAX_SAFE_INTEGER;

    if (currentDayOrder !== nextDayOrder) {
      return currentDayOrder - nextDayOrder;
    }

    const currentStartMinute = this.toMinute(this.formatTime(current.start_time));
    const nextStartMinute = this.toMinute(this.formatTime(next.start_time));

    if (currentStartMinute != null && nextStartMinute != null && currentStartMinute !== nextStartMinute) {
      return currentStartMinute - nextStartMinute;
    }

    if (currentStartMinute == null && nextStartMinute != null) {
      return 1;
    }

    if (currentStartMinute != null && nextStartMinute == null) {
      return -1;
    }

    return String(current.date).localeCompare(String(next.date));
  }

  private formatProjectTeachingTimes(times: ProjectResearchTeachingTimeDetail[]): string[] {
    return this.mapProjectTeachingTimesForPayload(times).map((time) => (
      `${this.getThaiDayName(time.day_of_week)} ${this.formatTime(time.start_time)} - ${this.formatTime(time.end_time)}`
    ));
  }

  private mapProjectTeachingTimesForPayload(
    times: ProjectResearchTeachingTimeDetail[],
  ): SubjectScheduleGroupProjectTeachingTime[] {
    const mappedTimes = times
      .map((time): SubjectScheduleGroupProjectTeachingTime | null => {
        const dayOfWeek = Number(time.day_of_week);
        const startTime = this.formatTime(time.start_time);
        const endTime = this.formatTime(time.end_time);

        if (!Number.isFinite(dayOfWeek) || startTime === '-' || endTime === '-') {
          return null;
        }

        return {
          day_of_week: dayOfWeek,
          start_time: startTime,
          end_time: endTime,
        };
      })
      .filter((time): time is SubjectScheduleGroupProjectTeachingTime => time != null);

    return sortProjectTeachingTimes(mappedTimes.map((time) => ({
      teachingDay: this.getThaiDayName(time.day_of_week),
      startTime: time.start_time,
      endTime: time.end_time,
    }))).map((time) => ({
      day_of_week: this.getDayOfWeek(time.teachingDay) ?? 0,
      start_time: time.startTime ?? '',
      end_time: time.endTime ?? '',
    }));
  }

  private mapSubjectManagementDetailMainStatus(status: string | null | undefined): { status: SubjectStatus; label: string } {
    return mapSubjectManagementDetailMainStatus(status);
  }

  private mapTeachingApprovalStatus(status: string | null | undefined): { status: SubjectStatus; label: string } {
    return mapTeachingApprovalStatus(status);
  }

  private mapSectionAggregatedStatus(
    statuses: (string | null | undefined)[],
    mode: 'shared' | 'independent',
    fallbackStatus?: string | null,
  ): { status: SubjectStatus; label: string } {
    return mapSectionAggregatedStatus(statuses, mode, fallbackStatus);
  }

  private normalizeStatus(status: string | null | undefined): string {
    return normalizeSubjectManagementStatus(status);
  }

  private getAssignmentApprovalStatus(
    assignment: SubjectScheduleAssignmentDetail | ProjectResearchAssignmentDetail,
  ): string {
    return this.displayOptionalText(
      assignment.approval?.status_code ??
      assignment.approval?.current_status?.code ??
      assignment.status
    );
  }

  private getAssignmentReturnReason(
    assignment: SubjectScheduleAssignmentDetail | ProjectResearchAssignmentDetail,
  ): string {
    const assignmentComment = this.displayOptionalText(assignment.comment);

    if (assignmentComment !== '') {
      return assignmentComment;
    }

    return this.displayOptionalText(assignment.approval?.latest_revision_action?.comment);
  }

  private getSectionApprovalStatus(
    assignments: SubjectScheduleAssignmentDetail[],
    fallbackStatus: string | null | undefined,
  ): string {
    return this.getHighestPriorityApprovalStatus(
      assignments.map((assignment) => this.getAssignmentApprovalStatus(assignment)),
      fallbackStatus
    );
  }

  private getProjectSectionApprovalStatus(
    assignments: ProjectResearchAssignmentDetail[],
    fallbackStatus: string | null | undefined,
  ): string {
    return this.getHighestPriorityApprovalStatus(
      assignments.map((assignment) => this.getAssignmentApprovalStatus(assignment)),
      fallbackStatus
    );
  }

  private getHighestPriorityApprovalStatus(
    statuses: string[],
    fallbackStatus: string | null | undefined,
  ): string {
    const normalizedStatuses = statuses
      .map((status) => normalizeTeachingApprovalStatus(status))
      .filter((status) => status !== '');
    const priority = [
      'special_revision_requested',
      'revision_requested',
      'ready_for_teacher',
      'imported_pending_fix',
      'teacher_confirmed',
      'draft',
      'education_head_confirmed',
      'vice_dean_confirmed',
      'locked',
      'approved',
    ];

    return priority.find((status) => normalizedStatuses.includes(status)) ??
      this.normalizeStatus(fallbackStatus);
  }

  private compareSectionValues(a: string, b: string): number {
    const numberA = Number(a);
    const numberB = Number(b);

    if (Number.isFinite(numberA) && Number.isFinite(numberB)) {
      return numberA - numberB;
    }

    return a.localeCompare(b, 'th');
  }

  private sumProjectStudentCount(assignments: ProjectResearchAssignmentDetail[]): number {
    return assignments.reduce((sum, assignment) => (
      sum
      + this.toNumber(assignment.primary_advisor_student_count)
      + this.toNumber(assignment.co_advisor_student_count)
    ), 0);
  }

  private sumProjectTeachingHours(assignments: ProjectResearchAssignmentDetail[]): number {
    return assignments.reduce((sum, assignment) => (
      sum + this.sumTeachingTimeHours(assignment.teaching_times ?? [])
    ), 0);
  }

  private sumTeachingTimeHours(times: ProjectResearchTeachingTimeDetail[]): number {
    return times.reduce((sum, time) => sum + this.toNumber(time.teaching_hours), 0);
  }

  private getThaiDayName(dayOfWeek: unknown): string {
    const day = Number(dayOfWeek);
    const dayNames: Record<number, string> = {
      0: 'วันอาทิตย์',
      1: 'วันจันทร์',
      2: 'วันอังคาร',
      3: 'วันพุธ',
      4: 'วันพฤหัสบดี',
      5: 'วันศุกร์',
      6: 'วันเสาร์',
      7: 'วันอาทิตย์',
    };

    return dayNames[day] ?? '-';
  }

  private getWeekdayFromDate(value: unknown): number | undefined {
    const dateValue = this.displayValue(value);

    if (dateValue === '-') {
      return undefined;
    }

    const date = new Date(`${dateValue}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    const jsDay = date.getDay();
    return jsDay === 0 ? 7 : jsDay;
  }

  private formatThaiDate(value: unknown): string {
    const dateValue = this.displayValue(value);

    if (dateValue === '-') {
      return '-';
    }

    const [yearText, month, day] = dateValue.split('-');
    const year = Number(yearText);

    if (!yearText || !month || !day || !Number.isFinite(year)) {
      return dateValue;
    }

    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year + 543}`;
  }

  private formatStatusUpdatedAt(value: string | null | undefined): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }

  private formatStatusUpdatedBy(value: SubjectScheduleStatusUpdatedByDetail | null | undefined): string {
    if (value == null) {
      return '-';
    }

    return this.displayOptionalText(value.display_name) ||
      this.displayOptionalText(value.full_name) ||
      this.displayOptionalText(value.role) ||
      '-';
  }

  private formatTime(value: unknown): string {
    const text = this.displayValue(value);

    if (text === '-') {
      return '-';
    }

    const match = text.match(/(\d{1,2}):(\d{2})/);

    if (match == null) {
      return text;
    }

    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }

  private buildTeachingTimeLabel(startTime: unknown, endTime: unknown): string {
    return `${this.displayValue(startTime)} - ${this.displayValue(endTime)}`;
  }

  private getEventValue(eventOrValue: ControlInputValue): string {
    if (eventOrValue instanceof Event) {
      const target = eventOrValue.target;

      if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) {
        return target.value;
      }

      return '';
    }

    return this.displayOptionalText(eventOrValue);
  }

  private sanitizeDigits(value: string): string {
    return value.replace(/\D/g, '');
  }

  private isBlank(value: unknown): boolean {
    const text = this.displayValue(value);
    return text === '-';
  }

  private toMinute(value: unknown): number | null {
    if (typeof value !== 'string') {
      return null;
    }

    const [hour, minute] = value.split(':').map(Number);

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      return null;
    }

    return hour * 60 + minute;
  }

  private withCurrentOption(value: unknown, options: readonly string[], includeEmpty = false): string[] {
    const currentValue = options === this.timeOptions
      ? this.formatTime(value)
      : this.displayValue(value);
    const baseOptions = includeEmpty ? ['', ...options] : [...options];

    if (currentValue === '-') {
      return Array.from(new Set(baseOptions));
    }

    return Array.from(new Set([currentValue, ...baseOptions]));
  }

  private ensureTimeOption(value: unknown): void {
    const time = this.formatTime(value);

    if (time === '-' || this.timeOptions.includes(time)) {
      return;
    }

    this.timeOptions = [...this.timeOptions, time].sort((current, next) => {
      const currentMinute = this.toMinute(current) ?? Number.MAX_SAFE_INTEGER;
      const nextMinute = this.toMinute(next) ?? Number.MAX_SAFE_INTEGER;

      if (currentMinute !== nextMinute) {
        return currentMinute - nextMinute;
      }

      return current.localeCompare(next);
    });
  }

  private displayYear(year: unknown): string {
    const parsedYear = Number(year);

    if (!Number.isFinite(parsedYear)) {
      return this.displayValue(year);
    }

    return String(parsedYear > 2400 ? parsedYear : parsedYear + 543);
  }

  private toChristianYear(value: unknown): string | null {
    const year = Number(value);

    if (!Number.isFinite(year)) {
      return null;
    }

    return String(year > 2400 ? year - 543 : year);
  }

  private displayNumber(value: unknown): string {
    const parsed = this.toNumber(value);

    if (!Number.isFinite(parsed) || parsed === 0) {
      return this.displayValue(value);
    }

    return Number.isInteger(parsed) ? String(parsed) : String(Number(parsed.toFixed(2)));
  }

  private displayOptionalText(value: unknown): string {
    const text = this.displayValue(value);
    return text === '-' ? '' : text;
  }

  private displayValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      return '-';
    }

    const text = String(value).trim();
    return text !== '' ? text : '-';
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toOptionalNumber(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private loadDisplayOptions(subjectId: number, fallbackSubject: SubjectDetail): void {
    forkJoin({
      studyModes: this.subjectScheduleService.getStudyModes().pipe(
        take(1),
        catchError(() => of([] as StudyModeModel[])),
      ),
      teachingTypes: this.subjectService.getTeachingType().pipe(
        take(1),
        catchError(() => of([] as TeachingTypeModel[])),
      ),
    }).subscribe(({ studyModes, teachingTypes }) => {
      this.subjectTypeOptions = studyModes
        .map((studyMode) => this.mapStudyModeToOption(studyMode))
        .filter((option): option is SubjectTypeOption => !!option);
      this.teachingTypeOptions = teachingTypes
        .map((teachingType) => this.mapTeachingTypeToOption(teachingType))
        .filter((option): option is TeachingTypeOption => !!option);
      this.loadSubjectDetails(subjectId, fallbackSubject);
    });
  }

  private mapStudyModeToOption(studyMode: StudyModeModel): SubjectTypeOption | null {
    const label = String(studyMode.name_th || studyMode.name_en || studyMode.code || '').trim();

    if (!label) {
      return null;
    }

    return {
      id: studyMode.id,
      label,
      code: studyMode.code,
    };
  }

  private getStudyModeLabel(studyMode: StudyModeModel | null | undefined, fallback?: unknown): string {
    const studyModeId = studyMode?.id ?? fallback;
    const optionLabel = this.getStudyModeLabelById(studyModeId);

    if (optionLabel != null) {
      return optionLabel;
    }

    const fallbackLabel = this.displayValue(
      studyMode?.name_th ??
      studyMode?.name_en ??
      studyMode?.code ??
      fallback
    );

    return this.getStudyModeLabelByCode(fallbackLabel) ?? fallbackLabel;
  }

  private getStudyModeLabelById(id: unknown): string | null {
    const studyModeId = Number(id);

    if (!Number.isFinite(studyModeId)) {
      return null;
    }

    return this.subjectTypeOptions.find((option) => option.id === studyModeId)?.label ?? null;
  }

  private getStudyModeLabelByCode(code: unknown): string | null {
    const studyModeCode = this.displayValue(code);

    if (studyModeCode === '-') {
      return null;
    }

    return this.subjectTypeOptions.find((option) => option.code === studyModeCode)?.label ?? null;
  }

  private getProjectResearchStudyModeCode(): string {
    return this.subjectTypeOptions.find((option) => (
      String(option.code ?? '').trim().toUpperCase() === 'SPC'
    ))?.code ?? 'SPC';
  }

  private getProjectResearchStudyModeLabel(): string {
    const projectResearchOption = this.subjectTypeOptions.find((option) => (
      String(option.code ?? '').trim().toUpperCase() === 'SPC'
    ));
    const subjectType = this.displayOptionalText(this.subject?.subjectType);

    return (
      projectResearchOption?.label
      ?? (subjectType.includes('โครงงาน') || subjectType.includes('วิจัย') ? subjectType : '')
    ) || 'รายวิชาโครงงาน / วิจัย';
  }

  private getTeachingTypeLabel(value: unknown): string {
    const teachingTypeId = Number(value);

    if (Number.isFinite(teachingTypeId)) {
      const optionLabel = this.teachingTypeOptions.find((option) => option.id === teachingTypeId)?.label;

      if (optionLabel != null) {
        return optionLabel;
      }
    }

    return this.displayValue(value);
  }

  private mapTeachingTypeToOption(teachingType: TeachingTypeModel): TeachingTypeOption | null {
    const label = String(teachingType.name_th || teachingType.name_en || teachingType.code || '').trim();

    if (!label) {
      return null;
    }

    return {
      id: teachingType.id,
      label,
    };
  }
}
